import { useRailwayHistory } from '../hooks/useRailwayHistory.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
// DESIGN: Chat remains the common conversational control plane. MCP context is
// a visual, read-first guidance signal; it does not modify chat backend modes.
import { Zap, Eye, ChevronRight, ChevronDown, ChevronUp, Copy, Mail, Plus, Share2, PanelRight, GitBranch, FileText, Lightbulb, AlertTriangle, Loader2, Cable } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { subscriberChatApi, useSubscriberChat, type Suggestion, type DeployedApp, type SubscriberLevel, type AutonomyLevel, type WizardQuestion, type WizardStatus, type EnrichedRequirements, type ConfirmationSummary, type SpecGenerationSession, type FactoryBuildView, type AnswerSource, type BuildStateResponse, type WizardAnswerResponse } from '../hooks/useSubscriberChat';
import WizardConfirmationCard from '../components/wizard/WizardConfirmationCard';
import { MarkdownContent } from '../components/chat/MarkdownContent';
import { CodexInvestigationCard, type CodexInvestigationState } from '../components/chat/CodexInvestigationCard.js';
import { LiveCodexChat } from '../components/chat/LiveCodexChat.js';
import { PortalCodexSession } from '../components/chat/PortalCodexSession.js';
import { readPortalCapability } from '../lib/portal-appserver.js';
import { gateInvestigationStart, investigationInstructions, codexIdempotencyKey, startCodexRun, readCodexRun, cancelCodexRun, listCodexRuns, startCodexConsent, reconcileCodexRun, discardCodexRun, CODEX_MAX_INSTRUCTIONS, readCodexTiers, STATIC_CODEX_TIERS, DEFAULT_CODEX_TIER, type CodexRunOutcome, type CodexTier, type CodexTierOption } from '../lib/chat-codex.js';
import type { CodexReceipt } from '../lib/codex-receipt.js';

/** A start this browser entered. `deepConsent` is the explicit per-run consent for a deep run; a retry of
 * the same message reuses it (same run, same key), a new message always asks again. */
type PendingCodexStart = { instructions: string; key: string; tier?: CodexTier; deepConsent?: boolean };
import { ProjectFixChecklist, composeFixSubmission, hasFixIntent, loadProjectTasks, selectFixableTasks, type ProjectSyncTask } from '../components/chat/ProjectFixChecklist';
import { fixProgressLabel, fixProgressUpdates, fixRunSummary, fixStartMessage, hasMergeIntent, mergeResultsMessage, repoWebUrl, type FixWatchCounts } from '../components/chat/fixProgress';
import { executeRemediationTask, isFixAlreadyRunning, mergeOpenFixPullRequests } from '../features/remediation/services/remediationApi';
import { ArtifactsPanel } from '../components/ArtifactsPanel';
import { commandsMatchingSlash, commandFromText, executeCommand, type Command } from '../lib/commands/registry';
import { takePendingEntrySpecFile } from '../features/uploads/pendingEntrySpecFile';
import { takePendingEntryRepo, type PendingEntryRepo } from '../features/uploads/pendingEntryRepo';
import { takeRiskRepairRequest, riskRepairTargetsFromPrompt, resolveRiskRepairTasks, type RiskRepairRequest } from '../lib/risk-repair-draft.js';
import { ProjectFixActivity } from '../components/chat/ProjectFixActivity.js';
import { LiveCodexSessionPanel } from '../components/chat/LiveCodexSessionPanel.js';
import { buildSpecExtractionResult, buildRepoIngestResult, specDigestFromParsedSpec } from '../lib/specExtraction';
import { BuildProgressCard, type LiveBuildProgress, type BuildProgressStep, type BuildStage, type BuildStageStatus, ghostButtonStyle, dimStyle } from "./SubscriberBuildProgress.js";
import { codexChatRoute, rememberCodexConsentReturn, takeCodexConsentRecheck } from '../lib/codex-consent-return.js';
import { readCodexGrant as readCodexGrantAfterConsent } from '../lib/chat-codex.js';
export { BuildProgressCard, sumCredits } from "./SubscriberBuildProgress.js";


// ─── Types ───────────────────────────────────────────────────

// ocn-S278-P0: added sync_find_fix to support entry-point routing
type UIMode = 'app_wizard' | 'build_progress' | 'app_management' | 'sync_find_fix';

const SPEC_MAX_CHARS = 200_000;
const ALLOWED_SPEC_EXT = ['.md', '.txt', '.html'];
// ocn-S816-P0: routed through the existing S569 wizard/parse-spec path
// (api.wizardParseSpec) instead of client-side FileReader text extraction.
const DOC_SPEC_EXT = ['.pdf', '.docx'];
const MAILTO_URL_MAX_LENGTH = 1_900;
// How often, and for how long, the chat checks on fixes it started.
const FIX_WATCH_INTERVAL_MS = 10_000;
const FIX_WATCH_MAX_MS = 30 * 60 * 1000;

/** Keep short UI titles readable when a spec heading is used as an app name. */
export function sanitizeShortTitle(title: string): string {
  return title
    .trim()
    .replace(/^#{1,6}\s*/, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[\`*_~]/g, '')
    .replace(/\s+/g, ' ')
    .trim() || 'Your app';
}

export interface ChatTranscriptContent {
  confirmation?: ConfirmationSummary | null;
  wizard?: { questions: WizardQuestion[]; answers: Record<string, unknown> } | null;
  build?: { status: string; liveUrl?: string | null; error?: string | null } | null;
}

export interface TranscriptMessage {
  role: 'user' | 'assistant';
  text: string;
  status?: 'failed';
  sources?: AnswerSource[];
  createdAt?: string | null;
}

/** Pure exporter: server messages are the canonical history; local messages may
 * add the current turn (including a failed, unsent turn). */
export function buildChatTranscript(
  bubbles: ChatBubble[] | TranscriptMessage[],
  content: ChatTranscriptContent = {},
): string {
  const messages = bubbles.map((bubble) =>
    `${bubble.role === 'user' ? `You${bubble.status === 'failed' ? ' [not sent]' : ''}` : 'Assistant'}: ${bubble.text.trim()}${bubble.sources?.length ? `\nSources: ${bubble.sources.filter(s => s.uri).map(s => `${s.title || 'Untitled'} — ${s.uri}`).join('; ')}` : ''}`,
  ).join('\n\n');
  const sections: string[] = [];
  const summary = content.confirmation;
  if (summary) {
    sections.push(['App summary (confirmed)', summary.productName && `Product: ${summary.productName}`, summary.oneLineDescription && `Description: ${summary.oneLineDescription}`, summary.coreFeatures?.length && `Features:\n${summary.coreFeatures.map(f => `- ${f.name}: ${f.description}`).join('\n')}`, summary.scope_exclusions?.length && `What is NOT being built:\n${summary.scope_exclusions.map(e => `- ${e}`).join('\n')}`].filter(Boolean).join('\n'));
  }
  if (content.wizard) {
    const { questions, answers } = content.wizard;
    sections.push(`Wizard questions and answers\n${questions.map(q => `Q: ${q.question}\nA: ${String(answers[q.id] ?? 'Not answered')}`).join('\n\n')}`);
  }
  if (content.build) {
    sections.push(`Final build status\nStatus: ${content.build.status}${content.build.liveUrl ? `\nLive URL: ${content.build.liveUrl}` : ''}${content.build.error ? `\nReason: ${content.build.error}` : ''}`);
  }
  return `Your App Factory chat transcript\n\n${messages || 'No messages yet.'}${sections.length ? `\n\n${sections.join('\n\n')}` : ''}`;
}


export interface ChatBubble {
  id: string;
  role: 'user' | 'assistant';
  // ocn-S310-P0: 'findings_detail' renders as inline collapsible per contract #4.
  // ocn-S346-P0: 'arch_review' renders the three-register narrative inline-expandably.
  kind: 'text' | 'examples' | 'autonomy_choice' | 'findings_detail' | 'arch_review';
  text: string;
  // Wiki/KB sources for grounded help answers. Rendered under the bubble.
  sources?: AnswerSource[];
  // ocn-S800-P0: a user bubble whose send failed. Never vanishes — rendered
  // with a visible "Not sent." state + Retry affordance instead.
  status?: 'failed';
  // ocn-S800-P0: client-generated idempotency key carried through retries so
  // a future server-side dedupe can recognize a resend of the same message.
  idempotencyKey?: string;
}

export interface StoredChatMessage {
  id: string;
  sender?: string;
  body?: string;
  metadata?: Record<string, unknown>;
}

/**
 * The server transcript is authoritative. A local turn is retained only when
 * the send explicitly failed; successful bubbles belong to the scoped server
 * history and must never follow a user into another chat session.
 */
export function mergeChatHistory(
  previous: ChatBubble[],
  messages: StoredChatMessage[],
): ChatBubble[] {
  const failedUnsent = previous.filter((bubble) => bubble.status === 'failed');
  return [
    ...messages.map((message) => ({
      id: message.id,
      role: message.sender === 'assistant' ? 'assistant' as const : 'user' as const,
      kind: 'text' as const,
      text: message.body ?? '',
      sources: Array.isArray(message.metadata?.sources) ? message.metadata.sources as AnswerSource[] : undefined,
      status: message.metadata?.status === 'failed' ? 'failed' as const : undefined,
    })),
    ...failedUnsent,
  ];
}

/** Append only durable Railway rows during progress refresh; local composer
 * bubbles remain intact. Stable server ids suppress repeated polling inserts. */
export function mergeRailwayProgress(previous:ChatBubble[],messages:StoredChatMessage[]):ChatBubble[]{
  const ids=new Set(previous.map(row=>row.id));
  const fresh=messages.filter(row=>['waiting','verifying','complete'].includes(String(row.metadata?.railwayWaitPhase))&&!ids.has(row.id));
  return [...previous,...mergeChatHistory([],fresh)];
}
export function hasPendingRailwayProgress(messages:StoredChatMessage[]):boolean{
  const phases=new Map<string,string>();
  for(const row of messages){const metadata=row.metadata;if(typeof metadata?.waitId!=='string'||!['waiting','verifying','complete'].includes(String(metadata.railwayWaitPhase)))continue;phases.set(metadata.waitId+':'+String(metadata.generation),String(metadata.railwayWaitPhase));}
  return [...phases.values()].some(phase=>phase!=='complete');
}

// ocn-S800-P0: pure — whether typed composer text should route to the
// wizard-answer endpoint. A build in progress (isBuildActive) always wins:
// stale/lingering wizard state must never intercept input once a build has
// started (was S796 §3, never landed — root cause: confirmBuild() left the
// local `wizard` object able to linger after clearing only the server
// pointer). Exported for direct unit testing without React.
export function shouldRouteComposerAsWizardAnswer(
  wizardStatus: WizardStatus | undefined,
  reviewing: boolean,
  isBuildActive: boolean,
): boolean {
  return wizardStatus === 'awaiting_answers' && !reviewing && !isBuildActive;
}

function newIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

interface WizardFlow {
  sessionId: string;
  questions: WizardQuestion[];
  answers: Record<string, unknown>;
  currentIndex: number;
  status: WizardStatus;
  requirements: EnrichedRequirements | null;
}

interface BuildTracker {
  factoryBuildId: string | null;
  specSessionId: string | null;
  specStatus: SpecGenerationSession['status'] | null;
  specEstimatedMinutes: number | null;
  pipelineRunId: string | null;
  appName: string;
}

// ─── ocn-S800-P0: server-authoritative mount rehydrate ────────
// Pure (deps injected) so it is unit-testable without React/network mocking.
// Precedence: server build-state ALWAYS wins when reachable (even "no active
// build" is authoritative and skips straight to wizard rehydrate); the
// client buildState blob is consulted ONLY when the build-state call itself
// throws (offline fallback) — closing the gap where a silently-failed
// best-effort persistBuildState() write left an in-flight/failed build
// undiscoverable on refresh.
export interface MountRehydrateDeps {
  getBuildState: () => Promise<{ activeBuild: BuildStateResponse | null }>;
  getWizardRequirements: (sessionId: string) => Promise<
    WizardAnswerResponse & { initialIdea: string; createdAt: string; completedAt?: string }
  >;
}

export interface MountRehydrateResult {
  build: BuildTracker | null;
  wizard: WizardFlow | null;
  confirmation: { summary: ConfirmationSummary | null; visible: boolean } | null;
  // true only when the build-state call itself failed (network/auth/etc.) —
  // the caller may then fall back to its own client-side blob.
  buildStateOffline: boolean;
}

export async function resolveMountRehydrate(
  persistedWizardSessionId: string | null,
  deps: MountRehydrateDeps,
): Promise<MountRehydrateResult> {
  let buildStateOffline = false;
  let activeBuild: BuildStateResponse | null = null;
  try {
    activeBuild = (await deps.getBuildState()).activeBuild;
  } catch {
    buildStateOffline = true;
  }
  if (activeBuild) {
    return {
      build: {
        factoryBuildId: activeBuild.factoryBuildId,
        specSessionId: activeBuild.specSessionId,
        specStatus: 'in_progress',
        specEstimatedMinutes: null,
        pipelineRunId: null,
        appName: activeBuild.appName || 'your app',
      },
      wizard: null,
      confirmation: null,
      buildStateOffline: false,
    };
  }

  // No server-known active build (or the call failed) — try wizard rehydrate.
  if (persistedWizardSessionId) {
    try {
      const req = await deps.getWizardRequirements(persistedWizardSessionId);
      if (req.status !== 'complete') {
        const answeredCount = Object.keys(req.answers ?? {}).length;
        const wizard: WizardFlow = {
          sessionId: persistedWizardSessionId,
          questions: req.questions ?? [],
          answers: req.answers ?? {},
          currentIndex: answeredCount,
          status: req.status,
          requirements: req.enrichedRequirements ?? null,
        };
        const confirmation = req.confirmationRequired && req.status === 'awaiting_confirmation'
          ? { summary: req.summary ?? null, visible: true }
          : null;
        return { build: null, wizard, confirmation, buildStateOffline };
      }
    } catch {
      /* stale/invalid wizard session — fall through silently */
    }
  }
  return { build: null, wizard: null, confirmation: null, buildStateOffline };
}

const EXAMPLE_IDEAS = [
  'A tool for gym owners to manage member check-ins and monthly billing',
  'A marketplace where photographers list and sell print-on-demand photos',
  'A simple CRM for freelance consultants tracking client leads and invoices',
  'An internal dashboard for my team to track weekly OKRs',
];

let bubbleCounter = 0;
function nextBubbleId(): string {
  bubbleCounter += 1;
  return `b${Date.now()}-${bubbleCounter}`;
}

interface InterviewSession {
  state: Record<string, unknown>;
  next: { id: string; prompt: string; options?: Array<{ value: string; label: string }>; multiSelect?: boolean } | null;
  complete: boolean;
  brandStep: InterviewSession['next'];
  summary?: { building: string[]; notBuilding: string[]; assumptions: Array<{ field: string; claim: string; basis: string; correctsTo: unknown }> };
  manifest?: Record<string, unknown>;
  brandTemplate?: string;
  error?: string | undefined;
}

function InterviewCard({ session, busy, onAnswer, onAssume, onFinalize }: {
  session: InterviewSession; busy: boolean;
  onAnswer: (value: unknown) => void;
  onAssume: (a: { field: string; value: unknown }) => void;
  onFinalize: () => void;
}): React.ReactElement {
  const q = session.brandStep && session.complete ? session.brandStep : session.next;
  const [selected, setSelected] = useState<string[]>([]);
  return <section style={sectionStyle} aria-live="polite">
    <h2 style={sectionTitleStyle}>Let’s get this right</h2>
    {session.summary && <div style={{ ...sectionStyle, marginBottom: 12 }}><strong>What I understand</strong>
      {session.summary.building.map(x => <div key={`b-${x}`}>Building: {x}</div>)}
      {session.summary.notBuilding.map(x => <div key={`n-${x}`} style={dimStyle}>Not building: {x}</div>)}
      {session.summary.assumptions.map(a => <div key={a.field} style={{ marginTop: 8 }}><div><strong>Assumption:</strong> {a.claim}</div><small>{a.basis}</small><button type="button" disabled={busy} onClick={() => onAssume({ field: a.field, value: a.correctsTo })} style={ghostButtonStyle}>That’s not right</button></div>)}
    </div>}
    {q ? <div style={sectionStyle}><p>{q.prompt}</p>{q.options?.map(o => <button key={o.value} type="button" disabled={busy} onClick={() => q.multiSelect ? setSelected(v => v.includes(o.value) ? v.filter(x => x !== o.value) : [...v, o.value]) : onAnswer(o.value)} style={{ ...chipStyle, margin: 4, background: selected.includes(o.value) ? 'var(--accent)' : undefined }}>{o.label}</button>)}
      {q.multiSelect && <button type="button" disabled={busy || selected.length === 0} onClick={() => { onAnswer(selected); setSelected([]); }} style={primaryButtonStyle}>Continue</button>}
    </div> : session.complete && !session.manifest ? <button type="button" disabled={busy} onClick={onFinalize} style={primaryButtonStyle}>Finish and start building</button> : <p>Interview complete.</p>}
  </section>;
}


interface SubscriberChatProps {
  chatSessionId?: string | null;
  /** A completed Sync explicitly selects the Track B conversation, never the legacy model. */
  syncId?: string;
  // ocn-S278-P0: initial mode from entry-point card (app_wizard, sync_find_fix, etc.)
  initialMode?: UIMode;
  // ocn-S278-P0: pre-selected project for sync_find_fix mode
  initialProjectId?: string | null;
  /** Project thread scope; unlike initialProjectId this resolves a durable thread. */
  projectId?: string | null;
  // ocn: page-level navigation from the chat (used to open plan selection when
  // an unpaid subscriber tries to build — the in-chat soft-gate for the paywall).
  onNavigate?: (page: string) => void;
  /** UI-only MCP guidance context. No OAuth, credentials, or backend mode is changed. */
  mcpContext?: boolean;
}

// ─── Autonomy sequence state ─────────────────────────────────
// turn === 0 means sequence complete (or not yet triggered).
type AutonomyTurn = 0 | 1 | 2 | 3;
interface AutonomySeq { turn: AutonomyTurn; pendingLean?: AutonomyLevel }

function buildTurn1(): string {
  return `Two ways to work together.\n\n` +
    `**(A) I don't want the details — just build it.** I'll handle the build and ship it. You stay out of the weeds and I surface outcomes and the occasional big decision.\n\n` +
    `**(B) I need to see details and approve merges.** You see the plan and approve changes before they go live.\n\n` +
    `Which fits how you want to start? (You can change this any time — just say "show me more detail" or "let me approve changes".)`;
}
function buildTurn2(lean: AutonomyLevel): string {
  if (lean === 'just_build') {
    return `Got it — I'll keep it high-level. You can say "show me the details" any time.\n\nWant a short heads-up before anything goes to production, or only if something needs a real decision?`;
  }
  return `I'll surface each change for your sign-off. Want that on everything, or only on production / merge-level changes?`;
}
function buildTurn3(level: AutonomyLevel): string {
  const label = level === 'just_build'
    ? "You've chosen **Build it — I'll stay out of the weeds**."
    : "You've chosen **Review & approve changes**.";
  return `${label}\n\nI'll work that way going forward. You can change this any time — just say "let me approve this" or "just do it, I trust you".`;
}
function leanFromAnswer(a: string): AutonomyLevel {
  const s = a.trim().toLowerCase();
  if (/^a\b|just build|don'?t (want|need).{0,20}detail|just do it|handle it/i.test(s)) return 'just_build';
  return 'review_and_approve';
}

// ocn-S278-P0 escape hatch (hard requirement): detect a mid-conversation request
// to change autonomy. Specific phrases only, so ordinary requests like "add a
// details page" do NOT trip it. Returns the requested level, or null if no
// autonomy-change intent is present.
function detectAutonomyIntent(text: string): AutonomyLevel | null {
  const s = text.trim().toLowerCase();
  // Wants MORE oversight → review_and_approve
  if (/show me (the|more) detail|let me (approve|review|see)|i want to (approve|review)|run it by me|check with me first|don'?t merge|slow down/.test(s)) {
    return 'review_and_approve';
  }
  // Wants LESS oversight → just_build
  if (/just (build|do) it|i trust you|stop asking|you decide|don'?t ask me|go ahead and ship|just ship it/.test(s)) {
    return 'just_build';
  }
  return null;
}

// ocn-S809-P0 (Part 4): the shared recovery state for ANY project/session
// load failure — never a bare error string. Plain-language explanation +
// the typed reason (when the server supplied one) + a path forward (Retry /
// Back to dashboard / Start a new chat). Matches the S792 failed-build
// treatment and the S800 recoverability bar: every failure state offers a
// way out, not a dead end. Exported for direct component testing.
export function RecoveryCard({
  reasonCode, onRetry, onBackToDashboard, onStartNewChat,
}: {
  reasonCode?: string | null;
  onRetry: () => void;
  onBackToDashboard: () => void;
  onStartNewChat: () => void;
}): React.ReactElement {
  const reasonLabel =
    reasonCode === 'project_not_yet_accessible'
      ? 'This project exists but isn\u2019t linked to your account yet.'
      : reasonCode === 'project_not_found'
      ? 'This project could not be found.'
      : null;
  return (
    <div style={recoveryCardStyle} role="alert">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <AlertTriangle size={16} style={{ color: 'var(--red, #f87171)', flexShrink: 0 }} aria-hidden="true" />
        <p style={recoveryTitleStyle}>We couldn&rsquo;t open this project.</p>
      </div>
      <p style={dimStyle}>{reasonLabel ?? 'Something went wrong loading this chat. Your data is safe.'}</p>
      <div style={recoveryActionsStyle}>
        <button type="button" className="btn btn-sm" onClick={onRetry}>Retry</button>
        <button type="button" className="btn btn-sm" onClick={onBackToDashboard}>Back to dashboard</button>
        <button type="button" className="btn btn-sm" onClick={onStartNewChat}>Start a new chat</button>
      </div>
    </div>
  );
}

export function ChatLoadingState({ onRetry }: { onRetry: () => void }): React.ReactElement {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => setElapsedSeconds((seconds) => seconds + 1), 1000);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <section role="status" aria-live="polite" style={{ ...sectionStyle, textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
      <div className="spinner" aria-hidden="true" style={{ width: 24, height: 24, margin: '0 auto 12px' }} />
      <strong>Opening your chat…</strong>
      <p style={{ ...dimStyle, margin: '6px 0 0' }}>
        {elapsedSeconds < 3
          ? 'Restoring your conversation and app context.'
          : `Still connecting (${elapsedSeconds}s). Your saved draft is safe.`}
      </p>
      {elapsedSeconds >= 10 && (
        <button type="button" onClick={onRetry} style={{ ...ghostButtonStyle, marginTop: 12 }}>
          Try again
        </button>
      )}
    </section>
  );
}

export function ChatComposerSubmit({ draft, busy, onSend }: { draft: string; busy: boolean; onSend: () => void }) {
  return <button onClick={onSend} disabled={busy || !draft.trim()} style={primaryButtonStyle} type="button">
    {busy ? <><InlineBusyMark /> Working...</> : 'Send'}
  </button>;
}

export function emptyChatEntry(projectId: string | null | undefined, messageCount: number, isBuildActive: boolean, chatMode?: string): 'project-checklist' | 'entry-cards' | null {
  if (isBuildActive) return null;
  if (projectId) return chatMode === 'sync_find_fix' ? 'project-checklist' : null;
  return messageCount > 0 ? null : 'entry-cards';
}

export interface QueuedComposerMessage { text: string; fixTaskIds: string[]; scope: string }

/** A queued turn belongs to the subscriber, project and thread that accepted
 * it. Switching scope discards stale turns rather than dispatching their ids. */
export function takeScopedComposerMessage(queue: QueuedComposerMessage[], scope: string): QueuedComposerMessage | null {
  while (queue.length > 0) {
    const next = queue.shift()!;
    if (next.scope === scope) return next;
  }
  return null;
}

export default function SubscriberChat(props: SubscriberChatProps): React.ReactElement {
  const { user } = useAuth();
  if (props.chatSessionId || props.projectId) return <PortalCandidate key={`${user?.id ?? 'anonymous'}:${props.chatSessionId ?? props.projectId}`} {...props}/>;
  return <LegacySubscriberChat {...props} />;
}

function PortalCandidate(props:SubscriberChatProps):React.ReactElement {
  const controller=useSubscriberChat(props.chatSessionId??null,props.projectId??null);
  const {session,loading,error}=controller;
  const [capability,setCapability]=useState<'loading'|'enabled'|'disabled'|'error'>('loading');
  const [failure,setFailure]=useState('');
  useEffect(()=>{
    const controller=new AbortController();setCapability('loading');setFailure('');
    if(!session)return()=>controller.abort();
    if(!session.projectId){setCapability('disabled');return()=>controller.abort();}
    if(session.entryKind==='build'&&session.chatMode!=='app_management'){setCapability('disabled');return()=>controller.abort();}
    void readPortalCapability(session.sessionId,controller.signal).then(enabled=>{if(!controller.signal.aborted)setCapability(enabled?'enabled':'disabled');}).catch(reason=>{if(!controller.signal.aborted){setCapability('error');setFailure(reason instanceof Error?reason.message:'Codex capability could not be verified.');}});
    return()=>controller.abort();
  },[session?.sessionId,session?.projectId,session?.entryKind,session?.chatMode]);
  if(loading||(!error&&capability==='loading'))return <p role="status">Opening your repository conversation…</p>;
  if(error||!session)return <p role="alert">This repository conversation could not be opened.</p>;
  if(capability==='error')return <div role="alert">Codex could not open this repository conversation: {failure}</div>;
  if(capability==='enabled')return <PortalCodexSession key={session.sessionId} chatId={session.sessionId} projectId={session.projectId} syncId={props.syncId} title={session.title} onNavigate={props.onNavigate}/>;
  if(props.syncId&&props.chatSessionId)return <LiveCodexChat chatSessionId={session.sessionId} syncId={props.syncId}/>;
  return <LegacySubscriberChatBody {...props} controller={controller}/>;
}

function LegacySubscriberChat(props:SubscriberChatProps):React.ReactElement {
  const controller=useSubscriberChat(props.chatSessionId??null,props.projectId??null);
  return <LegacySubscriberChatBody {...props} controller={controller}/>;
}

function LegacySubscriberChatBody({ chatSessionId, initialMode, initialProjectId, projectId, onNavigate, mcpContext = false,controller }: SubscriberChatProps&{controller:ReturnType<typeof useSubscriberChat>}): React.ReactElement {
  const { session, loading, error, errorCode, refresh: refreshSession, setLevel, setAutonomy,setMode:setSessionMode } = controller;
  const [renamedTitle, setRenamedTitle] = useState<string | null>(null);
  // Drawer state is per browser session and per chat, so a reply never closes it.
  const artifactsDrawerKey = `subscriber-chat:artifacts-drawer:${chatSessionId ?? projectId ?? 'new'}`;
  const [artifactsDrawerOpen, setArtifactsDrawerOpen] = useState(() => sessionStorage.getItem(artifactsDrawerKey) === 'open');
  const toggleArtifactsDrawer = useCallback(() => {
    setArtifactsDrawerOpen((open) => {
      const next = !open;
      sessionStorage.setItem(artifactsDrawerKey, next ? 'open' : 'closed');
      return next;
    });
  }, [artifactsDrawerKey]);
  const displayTitle = renamedTitle ?? session?.title ?? 'New chat';
  const renameChat = useCallback(async () => {
    if (!session) return;
    const title = window.prompt('Name this chat', displayTitle)?.trim();
    if (!title || title === displayTitle) return;
    try {
      const renamed = await api.renameSubscriberChat(session.sessionId, title);
      setRenamedTitle(renamed.title);
    } catch { /* Preserve the current title when the request fails. */ }
  }, [displayTitle, session]);
  // ocn-S321-P0: builder tier = full-bleed chat maximize (no outer maxWidth constraint).
  const { userTier, subscriber } = useAuth();
  const isBuilder = userTier === 'builder';
  const [mode, setMode] = useState<UIMode>(initialMode ?? 'app_wizard');
  const [bubbles, setBubbles] = useState<ChatBubble[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [apps, setApps] = useState<DeployedApp[]>([]);
  // The idle welcome must wait for this result; otherwise a returning
  // subscriber can be greeted as a first-time user while their apps are still
  // loading in the background.
  const [appsLoaded, setAppsLoaded] = useState(false);
  const [interview, setInterview] = useState<InterviewSession | null>(null);
  const [wizard, setWizard] = useState<WizardFlow | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [confirmationSummary, setConfirmationSummary] = useState<ConfirmationSummary | null>(null);
  const [build, setBuild] = useState<BuildTracker | null>(null);
  const [progress, setProgress] = useState<LiveBuildProgress | null>(null);
  const [liveApp, setLiveApp] = useState<{
    appName: string;
    url: string | null;
    buildStartedAt: number;
  } | null>(null);
  // ocn-S800-P0: draft persists across a refresh/close-reopen (per chat
  // session) until the message is actually sent successfully.
  const draftKey = `subscriber-chat:draft:${chatSessionId ?? projectId ?? 'new'}`;
  const [draft, setDraft] = useState(() => {
    try { return sessionStorage.getItem(draftKey) ?? ''; } catch { return ''; }
  });
  const riskRepairRequestRef = useRef<RiskRepairRequest | null>(null);
  useEffect(() => {
    if (!subscriber?.id || !projectId) return;
    const repairDraft = takeRiskRepairRequest(subscriber.id, projectId);
    riskRepairRequestRef.current = repairDraft;
    if (repairDraft) setDraft((current: string) => current ? `${current}\n\n${repairDraft.prompt}` : repairDraft.prompt);
  }, [subscriber?.id, projectId]);
  useEffect(() => {
    try {
      if (draft) sessionStorage.setItem(draftKey, draft);
      else sessionStorage.removeItem(draftKey);
    } catch { /* best-effort persistence only */ }
  }, [draft, draftKey]);
  // ocn-S800-P0: hoisted above sendMessage/confirmBuild — both need it, and
  // it only depends on `mode`/`build`, already in scope here.
  const isBuildActive = mode === 'build_progress' || build !== null;
  const [slashSelectedIndex, setSlashSelectedIndex] = useState(0);
  const [slashMenuDismissed, setSlashMenuDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [investigation, setInvestigation] = useState<CodexInvestigationState | null>(null);
  const [codexReceipt, setCodexReceipt] = useState<CodexReceipt | null>(null);
  const [codexBusy, setCodexBusy] = useState(false);
  // Investigation depth (B4). The menu comes from the server (MC advertisement or static list).
  const [codexTier, setCodexTier] = useState<CodexTier>(DEFAULT_CODEX_TIER);
  const [codexTiers, setCodexTiers] = useState<readonly CodexTierOption[]>(STATIC_CODEX_TIERS);
  const codexTierRef = useRef<CodexTier>(DEFAULT_CODEX_TIER);
  codexTierRef.current = codexTier;
  useEffect(() => {
    let live = true;
    void readCodexTiers().then((menu: readonly CodexTierOption[]) => {
      if (!live) return;
      setCodexTiers(menu);
      setCodexTier((current: CodexTier) => menu.some((t: CodexTierOption) => t.id === current) ? current
        : (menu.find((t: CodexTierOption) => !t.requiresConsent)?.id ?? DEFAULT_CODEX_TIER));
    });
    return () => { live = false; };
  }, []);
  // An unconfirmed start (same key + same instructions) awaiting "Check again". While it
  // exists no other investigation may start in this chat.
  const [pendingCodexStart, setPendingCodexStartState] = useState<PendingCodexStart | null>(null);
  const pendingCodexStartRef = useRef<PendingCodexStart | null>(null);
  const codexBusyRef = useRef(false);
  const codexReceiptRef = useRef<CodexReceipt | null>(null);
  codexReceiptRef.current = codexReceipt;
  // The server's unconfirmed start key for this chat when no local copy can replay it
  // (new tab or device). Only that same key may start until it is confirmed.
  const serverUnconfirmedKeyRef = useRef<string | null>(null);
  // Mirrors the ref for rendering: with no local copy, "Check again" asks the server to reconcile it.
  const [serverUnconfirmedKey, setServerUnconfirmedKeyState] = useState<string | null>(null);
  const setServerUnconfirmedKey = useCallback((value: string | null) => {
    serverUnconfirmedKeyRef.current = value;
    setServerUnconfirmedKeyState(value);
  }, []);
  const investigationGenerationRef = useRef(0);
  // Read through a ref so the stable callback below never captures a stale (or absent) subscriber.
  const subscriberIdRef = useRef<string | null>(null);
  subscriberIdRef.current = subscriber?.id ?? null;
  // No subscriber, no persistence: an unattributed saved start is never written or restored.
  const pendingStorageKey = (sessionId: string): string | null =>
    subscriberIdRef.current ? `ocn:codex-pending:${subscriberIdRef.current}:${sessionId}` : null;
  const setPendingCodexStart = useCallback((sessionId: string, value: PendingCodexStart | null) => {
    pendingCodexStartRef.current = value;
    setPendingCodexStartState(value);
    // Per-viewer convenience only: lets "Check again" survive a reload. Server state stays authoritative.
    try {
      const storageKey = pendingStorageKey(sessionId);
      if (storageKey && value) sessionStorage.setItem(storageKey, JSON.stringify(value));
      else if (storageKey) sessionStorage.removeItem(storageKey);
    } catch { /* storage unavailable: in-memory only */ }
  }, []);
  // Reload/reconnect recovers the owned run from server state; it never starts one.
  const codexProjectId = projectId ?? session?.projectId ?? null;
  const recoverCodexRuns = useCallback(async (sessionId: string, generation: number) => {
    if (!codexProjectId) return;
    const entries = await listCodexRuns(sessionId);
    if (generation !== investigationGenerationRef.current || !entries?.length) return;
    const latest = entries[0];
    if (latest.kind === 'run') {
      setCodexReceipt(latest.receipt);
      // One status read of a still-live run, so a run that finished meanwhile unlocks the chat
      // without a manual refresh. A read only: never a start, never retried. On failure the
      // last confirmed receipt stays and "Refresh status" remains available.
      if (!['completed', 'failed', 'cancelled'].includes(latest.receipt.status)) {
        const fresh = await readCodexRun(sessionId, latest.receipt.run_id);
        if (generation === investigationGenerationRef.current && fresh.ok) setCodexReceipt(fresh.receipt);
      }
    } else if (latest.kind === 'unconfirmed') {
      setInvestigation('uncertain');
      setServerUnconfirmedKey(latest.idempotencyKey);
      try {
        const storageKey = pendingStorageKey(sessionId);
        const stored: unknown = storageKey ? JSON.parse(sessionStorage.getItem(storageKey) ?? 'null') : null;
        if (stored && typeof stored === 'object' && (stored as { key?: unknown }).key === latest.idempotencyKey
          && typeof (stored as { instructions?: unknown }).instructions === 'string') {
          // The saved start keeps its depth; deep consent is restored only with the deep tier it was given for.
          const saved = stored as { instructions: string; tier?: unknown; deepConsent?: unknown };
          const savedTier: CodexTier | undefined = saved.tier === 'light' || saved.tier === 'standard' || saved.tier === 'deep' ? saved.tier : undefined;
          const restored: PendingCodexStart = { instructions: saved.instructions, key: latest.idempotencyKey,
            ...(savedTier ? { tier: savedTier } : {}), ...(savedTier === 'deep' && saved.deepConsent === true ? { deepConsent: true } : {}) };
          pendingCodexStartRef.current = restored;
          setPendingCodexStartState(restored);
        }
      } catch { /* nothing to restore: "Check again" reconciles the server's stored copy */ }
    }
  }, [codexProjectId, setServerUnconfirmedKey]);
  useEffect(() => {
    const generation = ++investigationGenerationRef.current;
    setInvestigation(null);
    setCodexReceipt(null);
    pendingCodexStartRef.current = null;
    setPendingCodexStartState(null);
    setServerUnconfirmedKey(null);
    const sessionId = session?.sessionId;
    if (!sessionId || !codexProjectId) return;
    void recoverCodexRuns(sessionId, generation);
  }, [subscriber?.id, session?.sessionId, codexProjectId, recoverCodexRuns, setServerUnconfirmedKey]);
  // Apply a Codex outcome: a verified receipt replaces the card; a failure keeps the
  // last confirmed receipt visible and never triggers an automatic retry.
  const applyCodexOutcome = useCallback((generation: number, outcome: CodexRunOutcome) => {
    if (generation !== investigationGenerationRef.current) return;
    if (outcome.ok) { setCodexReceipt(outcome.receipt); setInvestigation(null); return; }
    setInvestigation(outcome.failure);
  }, []);
  // Enter (or re-enter) one start with a fixed key. A verified receipt or a definite
  // refusal clears the pending start; an uncertain outcome keeps it for "Check again".
  const runCodexStart = useCallback(async (sessionId: string, generation: number, start: PendingCodexStart) => {
    setPendingCodexStart(sessionId, start);
    codexBusyRef.current = true;
    setCodexBusy(true);
    try {
      const outcome = await startCodexRun(sessionId, start.instructions, start.key,
        { ...(start.tier ? { tier: start.tier } : {}), ...(start.deepConsent ? { deepConsent: true } : {}) });
      // Definite outcomes clear the pending start; only an uncertain one keeps it for "Check again".
      if (outcome.ok || outcome.failure === 'denied' || outcome.failure === 'unavailable' || outcome.failure === 'in_progress') {
        setPendingCodexStart(sessionId, null);
        if (serverUnconfirmedKeyRef.current === start.key) setServerUnconfirmedKey(null);
      }
      applyCodexOutcome(generation, outcome);
      return outcome;
    } finally { codexBusyRef.current = false; setCodexBusy(false); }
  }, [applyCodexOutcome, setPendingCodexStart, setServerUnconfirmedKey]);
  const checkCodexStartAgain = useCallback(async () => {
    const start = pendingCodexStartRef.current;
    if (!session || !start || codexBusyRef.current) return;
    await runCodexStart(session.sessionId, investigationGenerationRef.current, start);
  }, [session, runCodexStart]);
  // "Check again" with no local copy (new tab or device): the server re-enters its stored start
  // for this chat with the SAME key. Explicit user action only; never automatic, never a new key.
  const reconcileCodexStart = useCallback(async () => {
    if (!session || codexBusyRef.current || pendingCodexStartRef.current || !serverUnconfirmedKeyRef.current) return;
    const sessionId = session.sessionId;
    const generation = investigationGenerationRef.current;
    codexBusyRef.current = true;
    setCodexBusy(true);
    try {
      const outcome = await reconcileCodexRun(sessionId);
      if (generation !== investigationGenerationRef.current) return;
      if (!outcome.ok && outcome.failure === 'not_pending') {
        // Settled elsewhere: show what the server now holds for this chat.
        setServerUnconfirmedKey(null);
        setInvestigation(null);
        await recoverCodexRuns(sessionId, generation);
        return;
      }
      // Only a verified receipt confirms the key; any other outcome leaves it unconfirmed on the server.
      if (outcome.ok) setServerUnconfirmedKey(null);
      applyCodexOutcome(generation, outcome as CodexRunOutcome);
    } finally { codexBusyRef.current = false; setCodexBusy(false); }
  }, [session, applyCodexOutcome, recoverCodexRuns, setServerUnconfirmedKey]);
  // "Discard stuck investigation": explicit, confirmed user action only. The server asks Mission
  // Control first; a real run is shown (never discarded), and only MC's explicit "no run for this
  // key" abandons the start and frees the chat. Nothing is retried automatically.
  const discardCodexStart = useCallback(async () => {
    if (!session || codexBusyRef.current || (!pendingCodexStartRef.current && !serverUnconfirmedKeyRef.current)) return;
    let confirmed = false;
    try {
      confirmed = window.confirm('Discard the unconfirmed investigation in this chat? Mission Control is checked first: if it is actually running, it will be shown instead of discarded.');
    } catch { confirmed = false; }
    if (!confirmed) return;
    const sessionId = session.sessionId;
    const generation = investigationGenerationRef.current;
    codexBusyRef.current = true;
    setCodexBusy(true);
    try {
      const outcome = await discardCodexRun(sessionId);
      if (generation !== investigationGenerationRef.current) return;
      if (outcome.ok || outcome.failure === 'not_pending') {
        // Settled (abandoned, accepted, or already settled elsewhere): the chat is no longer held.
        setPendingCodexStart(sessionId, null);
        setServerUnconfirmedKey(null);
      }
      if ('abandoned' in outcome) { setInvestigation(null); return; }
      if (!outcome.ok && outcome.failure === 'not_pending') {
        setInvestigation(null);
        await recoverCodexRuns(sessionId, generation);
        return;
      }
      applyCodexOutcome(generation, outcome as CodexRunOutcome);
    } finally { codexBusyRef.current = false; setCodexBusy(false); }
  }, [session, applyCodexOutcome, recoverCodexRuns, setPendingCodexStart, setServerUnconfirmedKey]);
  const connectCodex = useCallback(async () => {
    if (codexBusyRef.current) return;
    codexBusyRef.current = true;
    setCodexBusy(true);
    try {
      const consent = await startCodexConsent();
      if ('url' in consent) {
        // MC returns to a fixed URL; remember this chat so the return lands back here (same tab, 15 min).
        const route = codexChatRoute(projectId ?? null, session?.chatSessionId ?? null);
        if (route && subscriberIdRef.current) rememberCodexConsentReturn(route, subscriberIdRef.current);
        window.location.assign(consent.url);
      }
      else setInvestigation(consent.error === 'rate_limited' ? 'consent_rate_limited'
        : consent.error === 'consent_pending' ? 'consent_pending' : 'verification_unavailable');
    } finally { codexBusyRef.current = false; setCodexBusy(false); }
  }, [projectId, session?.chatSessionId]);
  // Back from Mission Control consent in the chat that started it: re-check the grant ONCE and show the
  // result. Never starts a run: the user re-sends /investigate.
  const consentRecheckRef = useRef(false);
  useEffect(() => {
    if (consentRecheckRef.current || !session || !subscriber?.id) return;
    const route = codexChatRoute(projectId ?? null, session.chatSessionId ?? null);
    if (!route) return;
    consentRecheckRef.current = true;
    if (!takeCodexConsentRecheck(subscriber.id, route)) return;
    const generation = investigationGenerationRef.current;
    void readCodexGrantAfterConsent().then((grant) => {
      if (generation !== investigationGenerationRef.current) return;
      setInvestigation(grant === 'granted' ? 'connected' : grant);
    });
  }, [session, subscriber?.id, projectId]);
  const refreshCodexRun = useCallback(async () => {
    if (!session || !codexReceipt) return;
    const generation = investigationGenerationRef.current;
    setCodexBusy(true);
    try { applyCodexOutcome(generation, await readCodexRun(session.sessionId, codexReceipt.run_id)); }
    finally { setCodexBusy(false); }
  }, [session, codexReceipt, applyCodexOutcome]);
  const stopCodexRun = useCallback(async () => {
    if (!session || !codexReceipt) return;
    const generation = investigationGenerationRef.current;
    setCodexBusy(true);
    try { applyCodexOutcome(generation, await cancelCodexRun(session.sessionId, codexReceipt.run_id)); }
    finally { setCodexBusy(false); }
  }, [session, codexReceipt, applyCodexOutcome]);
  // ocn-S816-P0: extraction-progress working state ("Reading your spec…")
  // shown while handleFileAttach awaits the enrichment call. Separate from
  // `busy` so its aria-live text/elapsed heartbeat only render for this one
  // operation, not every busy state.
  const [extracting, setExtracting] = useState(false);
  const extractionStartedAtRef = useRef<number | null>(null);
  const [extractionElapsedSec, setExtractionElapsedSec] = useState(0);
  useEffect(() => {
    if (!extracting) { extractionStartedAtRef.current = null; return; }
    extractionStartedAtRef.current = Date.now();
    setExtractionElapsedSec(0);
    const iv = setInterval(() => {
      const startedAt = extractionStartedAtRef.current;
      if (startedAt) setExtractionElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(iv);
  }, [extracting]);
  // Composer submissions made while a request is running are sent in order once
  // that request settles. The field itself remains usable throughout.
  const [queuedMessageCount, setQueuedMessageCount] = useState(0);
  const composerScope = JSON.stringify([subscriber?.id ?? null, session?.sessionId ?? null, projectId ?? session?.projectId ?? null]);
  const composerScopeRef = useRef(composerScope);
  composerScopeRef.current = composerScope;
  const pendingMessagesRef = useRef<QueuedComposerMessage[]>([]);
  const [dismissedBanner, setDismissedBanner] = useState(false);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  // Live request composed from the project checklist (null when nothing is checked).
  const checklistDraftRef = useRef<string | null>(null);
  // Live ids of the checked sync tasks (fix mode sends these to remediation).
  const checklistIdsRef = useRef<string[]>([]);
  // Open tasks behind the checklist, so a typed 'fix all' can mean all of them
  // (not only the pre-checked critical + high ones).
  const checklistTasksRef = useRef<ProjectSyncTask[]>([]);
  useEffect(() => {
    checklistIdsRef.current = [];
    checklistDraftRef.current = null;
    checklistTasksRef.current = [];

  }, [composerScope]);
  // Latest bubbles, for sending the prior turns along with a help question.
  const bubblesRef = useRef<ChatBubble[]>([]);
  useEffect(() => { bubblesRef.current = bubbles; }, [bubbles]);
  // ocn-S800-P0 (re-lands hollow ocn-S796 §C.1): close the share dropdown on
  // an outside click or Escape. Copy/Email chat already close it synchronously
  // on click (S792/S793) — only this hygiene was missing.
  useEffect(() => {
    if (!shareMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target as Node)) setShareMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setShareMenuOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [shareMenuOpen]);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // ocn-S278-P0: opening autonomy sequence state
  const [autonomySeq, setAutonomySeq] = useState<AutonomySeq>({ turn: 0 });
  const threadEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ocn-S792-P0: guards the factory-build poller's terminal-status chat
  // bubble (live/failed/cancelled) against a duplicate append for the same
  // factoryBuildId — e.g. a dev-mode StrictMode double effect-invoke, or any
  // other path that could re-run the terminal branch within one mount.
  const terminalNotifiedBuildIdsRef = useRef<Set<string>>(new Set());

  const appendBubble = useCallback((
    role: ChatBubble['role'],
    text: string,
    kind: ChatBubble['kind'] = 'text',
    sources?: AnswerSource[],
  ) => {
    setBubbles(prev => [...prev, { id: nextBubbleId(), role, kind, text, sources }]);
  }, []);

  // Fixes this chat started, watched until each opens its pull request, is
  // fixed, or fails. The chat promised to post each pull request as it opened
  // and never did (canary 2026-10-03).
  const fixWatchRef = useRef<{ timer: ReturnType<typeof setInterval> | null; pending: Set<string>; deadline: number; busy: boolean; total: number; opened: number; merged: number; failed: number; paused: number }>({ timer: null, pending: new Set(), deadline: 0, busy: false, total: 0, opened: 0, merged: 0, failed: 0, paused: 0 });
  // What the live progress line shows while fixes are in flight (null = nothing running).
  const [fixWatchView, setFixWatchView] = useState<FixWatchCounts | null>(null);
  // How many issues are ticked in the checklist, for the dedicated checklist action.

  const [fixActivityRevision, setFixActivityRevision] = useState(0);
  const [checklistRevision, setChecklistRevision] = useState(0);
  const refreshChecklist = useCallback(() => setChecklistRevision((value: number) => value + 1), []);
  const refreshFixTasks = useCallback(() => {
    setFixActivityRevision((value: number) => value + 1);
    refreshChecklist();
  }, [refreshChecklist]);
  const fixWatchGenerationRef = useRef(0);
  const fixProjectIdRef = useRef<string | null>(null);
  useEffect(() => { fixProjectIdRef.current = projectId ?? session?.projectId ?? null; }, [projectId, session?.projectId]);
  // Stop watching when the chat changes or closes, so nothing posts into another thread.
  useEffect(() => {
    const watch = fixWatchRef.current;
    return () => {
      fixWatchGenerationRef.current += 1;
      if (watch.timer) clearInterval(watch.timer);
      watch.timer = null;
      watch.pending.clear();
      setFixWatchView(null);
    };
  }, [session?.sessionId, projectId]);
  const refreshRailwayHistory=useRailwayHistory<StoredChatMessage>(
    session?.sessionId,session?.railwayProgressEnabled===true,
    subscriberChatApi.getMessages,
    (rows:StoredChatMessage[])=>setBubbles((previous:ChatBubble[])=>mergeRailwayProgress(previous,rows)),
    hasPendingRailwayProgress,
  );
  const watchFixes = useCallback((watchProjectId: string, ids: string[]) => {
    const watch = fixWatchRef.current;
    const generation = fixWatchGenerationRef.current;
    if (!watch.timer) { watch.total = 0; watch.opened = 0; watch.merged = 0; watch.failed = 0; watch.paused = 0; }
    for (const id of ids) {
      if (!watch.pending.has(id)) { watch.pending.add(id); watch.total += 1; }
    }
    watch.deadline = Date.now() + FIX_WATCH_MAX_MS;
    const publish = () => setFixWatchView(watch.pending.size > 0
      ? { total: watch.total, remaining: watch.pending.size, opened: watch.opened, merged: watch.merged, failed: watch.failed, paused: watch.paused }
      : null);
    publish();
    if (watch.timer || watch.pending.size === 0) return;
    let repoUrl: string | null = null;
    void api.getProject(watchProjectId)
      .then((project) => { repoUrl = repoWebUrl((project as unknown as { repo?: { owner?: string | null; name?: string | null } }).repo); })
      .catch(() => { /* messages fall back to a plain pull request number */ });
    const stop = () => { if (watch.timer) clearInterval(watch.timer); watch.timer = null; setFixWatchView(null); };
    const tick = async () => {
      if (watch.busy) return;
      watch.busy = true;
      try {
        const allTasks = await loadProjectTasks(watchProjectId);
        await refreshRailwayHistory();
        if (generation !== fixWatchGenerationRef.current || watchProjectId !== fixProjectIdRef.current) return;
        // Keep a typed 'fix all' current: only issues with no fix already in flight.
        checklistTasksRef.current = selectFixableTasks(allTasks);
        const update = fixProgressUpdates(allTasks, watch.pending, repoUrl);
        for (const id of update.settledIds) watch.pending.delete(id);
        watch.opened += update.opened;
        watch.merged += update.merged;
        watch.failed += update.failed;
        watch.paused += update.paused ?? 0;
        if (update.message) appendBubble('assistant', update.message);
        publish();
      } catch {
        /* transient: the next tick retries */
      } finally {
        watch.busy = false;
      }
      if (generation !== fixWatchGenerationRef.current) return;
      if (watch.pending.size === 0) {
        stop();
        const summary = fixRunSummary({ total: watch.total, remaining: 0, opened: watch.opened, merged: watch.merged, failed: watch.failed, paused: watch.paused });
        if (summary) appendBubble('assistant', summary);
        return;
      }
      if (Date.now() > watch.deadline) {
        const left = watch.pending.size;
        watch.pending.clear();
        stop();
        appendBubble('assistant', `${left} fix${left === 1 ? ' is' : 'es are'} still running. Check your repo on GitHub for ${left === 1 ? 'its pull request' : 'their pull requests'}.`);
      }
    };
    watch.timer = setInterval(() => { void tick(); }, FIX_WATCH_INTERVAL_MS);
  }, [appendBubble, refreshRailwayHistory]);
  const watchFixesRef = useRef(watchFixes);
  useEffect(() => { watchFixesRef.current = watchFixes; }, [watchFixes]);

  // ocn resumability: mirror the active build handle into the durable
  // subscriber_chat.buildState blob so a reload/crash (e.g. a failed spec
  // upload that already created the factory_build) can re-enter the in-progress
  // or failed project instead of dead-ending on the generic welcome.
  // Best-effort: persistence must NEVER break the build flow, so failures are
  // swallowed (the next state change retries).
  const persistBuildState = useCallback((b: BuildTracker | null) => {
    if (!session) return;
    const payload: Record<string, unknown> = b?.factoryBuildId
      ? {
          factoryBuildId: b.factoryBuildId,
          specSessionId: b.specSessionId,
          specStatus: b.specStatus,
          appName: b.appName,
        }
      : {};
    void subscriberChatApi.updateSession(session.sessionId, { buildState: payload }).catch(() => {
      /* non-fatal: resumability persistence is best-effort */
    });
  }, [session]);

  // ocn: mirror the active wizard session pointer into subscriber_chat.wizardState
  // so a reload mid-wizard (before a build is fired) resumes into the wizard
  // instead of restarting. Best-effort; never blocks the flow.
  const persistWizardState = useCallback((wizardSessionId: string | null) => {
    if (!session) return;
    const payload: Record<string, unknown> = wizardSessionId ? { wizardSessionId } : {};
    void subscriberChatApi.updateSession(session.sessionId, { wizardState: payload }).catch(() => {
      /* non-fatal: wizard resumability persistence is best-effort */
    });
  }, [session]);

  // Grounded help: answer a common question from the public knowledge corpus
  // and surface the wiki/KB links it used. Used by the technical_support branch
  // and the no-signal fall-through, so "how does X work" questions get a real
  // answer with citations instead of a dead-end nudge.
  const answerHelp = useCallback(async (message: string) => {
    try {
      // Send the prior turns too: the answer endpoint is otherwise stateless, so a
      // follow-up ('what happened?') had nothing to refer to.
      const history = bubblesRef.current
        .filter((b) => typeof b.text === 'string' && b.text.trim().length > 0)
        .slice(-12)
        .map((b) => ({ role: b.role, text: b.text.slice(0, 800) }));
      const res = await subscriberChatApi.answer(message, { sessionId: session?.sessionId, history });
      // ocn-S310-P0: findings drill-down answers are rendered inline-expandably (contract #4).
      const kind = res.findings_detail ? 'findings_detail' : 'text';
      appendBubble(
        'assistant',
        res.answer || "I couldn't find an answer to that — try rephrasing, or tell me what you're trying to do.",
        kind,
        res.sources,
      );
    } catch (err) {
      appendBubble('assistant', `Sorry, I couldn't look that up: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [session?.sessionId, appendBubble]);

  const refreshSuggestions = useCallback(async () => {
    try {
      const r = await subscriberChatApi.getSuggestions({
        chatSessionId: chatSessionId ?? undefined,
        subscriberChatId: session?.sessionId,
      });
      setSuggestions(r.suggestions);
    } catch {
      setSuggestions([]);
    }
  }, [chatSessionId, session?.sessionId]);

  const refreshApps = useCallback(async () => {
    try {
      const r = await subscriberChatApi.listApps();
      setApps(r.apps);
    } catch {
      // A failed app-list request must not block the chat. Treat it as an
      // unknown/empty list for this visit and keep the composer available.
      setApps([]);
    } finally {
      setAppsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!session) return;
    setAppsLoaded(false);
    void refreshApps();
  }, [session?.sessionId, refreshApps]);

  const transcriptContent = useCallback((): ChatTranscriptContent => ({
    confirmation: confirmationSummary,
    wizard: wizard ? { questions: wizard.questions, answers: wizard.answers } : null,
    build: progress ? {
      status: progress.status,
      liveUrl: liveApp?.url ?? null,
      error: progress.friendlyError,
    } : null,
  }), [confirmationSummary, liveApp, progress, wizard]);

  const copyTranscript = useCallback(async (): Promise<boolean> => {
    const transcript = buildChatTranscript(bubbles, transcriptContent());
    try {
      await navigator.clipboard.writeText(transcript);
      setExportStatus('Chat copied.');
      return true;
    } catch {
      setExportStatus('Could not copy the chat.');
      return false;
    }
  }, [bubbles, transcriptContent]);

  const emailTranscript = useCallback(async () => {
    const transcript = buildChatTranscript(bubbles, transcriptContent());
    let body = transcript;
    let copiedFullTranscript = false;
    let mailto = `mailto:?subject=${encodeURIComponent('Your App Factory chat transcript')}&body=${encodeURIComponent(body)}`;

    if (mailto.length > MAILTO_URL_MAX_LENGTH) {
      body = 'Your App Factory chat transcript is too long to include in an email link. The full transcript has been copied to your clipboard.';
      copiedFullTranscript = await copyTranscript();
      mailto = `mailto:?subject=${encodeURIComponent('Your App Factory chat transcript')}&body=${encodeURIComponent(body)}`;
    }

    window.location.href = mailto;
    setExportStatus(copiedFullTranscript ? 'Email opened; full chat copied.' : 'Email draft opened.');
  }, [bubbles, copyTranscript, transcriptContent]);

  // Server history is authoritative across mounts. A successful local turn
  // belongs to that server transcript; only an explicitly failed send remains
  // local so it can be retried without leaking into a different session.
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    void subscriberChatApi.getMessages(session.sessionId).then((messages) => {
      if (cancelled || !messages.length) return;
      setBubbles((previous) => mergeChatHistory(previous, messages));
      void refreshRailwayHistory(messages);
    }).catch(() => { /* history is best-effort; current local turns remain exportable */ });
    return () => { cancelled = true; };
  }, [session?.sessionId]);

  // ocn-S278-P0: if autonomyLevel is null, skip the standard welcome and open the autonomy sequence.
  useEffect(() => {
    if (!session || !appsLoaded) return;
    if (bubbles.length === 0) {
      if (session.autonomyLevel === null || session.autonomyLevel === undefined) {
        // Kick off the opening autonomy sequence (turn 1).
        if (projectId ?? session.projectId) {
          // Project chats open on the post-sync fix checklist. Do NOT start the
          // autonomy Q&A here: its turn-1 intercept would swallow the drafted
          // 'Please fix...' message as an A/B answer. Forge changes land as PRs the
          // subscriber merges, so review-and-approve is the honest default.
          void setAutonomy('review_and_approve');
        } else {
          setAutonomySeq({ turn: 1 });
          appendBubble('assistant', 'What would you like to build today? Describe your app idea and I’ll help build it.', 'autonomy_choice');
        }
      } else {
        // ocn-S800-P0: server-authoritative rehydrate. The client buildState
        // blob (persistedBuild below) is consulted ONLY as an offline
        // fallback when the build-state call itself fails — see
        // resolveMountRehydrate's contract.
        const persistedBuild = (session.buildState ?? {}) as {
          factoryBuildId?: string | null;
          specSessionId?: string | null;
          specStatus?: BuildTracker['specStatus'];
          appName?: string;
        };
        const persistedWizardSessionId = (session.wizardState as { wizardSessionId?: string })?.wizardSessionId ?? null;
        void (async () => {
          const result = await resolveMountRehydrate(persistedWizardSessionId, {
            getBuildState: ()=>session.chatSessionId?subscriberChatApi.getBuildState(session.chatSessionId):Promise.resolve({activeBuild:null}),
            getWizardRequirements: subscriberChatApi.getWizardRequirements,
          });
          if (result.build) {
            setBuild(result.build);
            setMode('build_progress');
            appendBubble('assistant', `Welcome back — picking up your build for **${result.build.appName}**. Let me refresh where it's at…`);
          } else if (result.wizard) {
            setWizard(result.wizard);
            setMode('app_wizard');
            if (result.confirmation) {
              setConfirmationSummary(result.confirmation.summary);
              setConfirmationVisible(result.confirmation.visible);
            }
            appendBubble('assistant', "Welcome back — let's pick up your app setup where you left off.");
          } else if (result.buildStateOffline && persistedBuild.factoryBuildId) {
            // Offline fallback: the server call itself failed — the client blob
            // is the only signal we have.
            const resumedName = persistedBuild.appName || 'your app';
            setBuild({
              factoryBuildId: persistedBuild.factoryBuildId,
              specSessionId: persistedBuild.specSessionId ?? null,
              specStatus: persistedBuild.specStatus ?? 'in_progress',
              specEstimatedMinutes: null,
              pipelineRunId: null,
              appName: resumedName,
            });
            setMode('build_progress');
            appendBubble('assistant', `Welcome back — picking up your build for **${resumedName}**. Let me refresh where it's at…`);
          } else {
            const returning = (session.onboardingState as { step?: string; skipped?: boolean })?.skipped
              || apps.length > 0;
            appendBubble(
              'assistant',
              returning
                ? "Welcome back! Tell me what you'd like to build, or ask about one of your apps."
                : "Hi! I'm your AI app factory. Describe any app idea in plain English — I'll handle the rest.",
            );
          }
        })();
      }
    }
    void refreshSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId, appsLoaded, apps.length]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [bubbles.length, progress?.overallProgress, liveApp?.appName, mode]);

  useEffect(() => {
    void refreshSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ─── Wizard flow ────────────────────────────────────────────

  const startWizard = useCallback(async (idea: string) => {
    setBusy(true);
    try {
      const res = await subscriberChatApi.interviewNext({}) as InterviewSession;
      setInterview({ ...res, state: {}, error: undefined });
      setMode('app_wizard');
      appendBubble('assistant', 'Great — I’ll ask a few focused questions so the build matches what you mean.');
      // Keep the original idea visible as an instant echo/context, while every
      // scope decision comes from the server-authoritative interview state.
      if (idea.trim()) appendBubble('user', idea.trim());
    } catch (err) {
      appendBubble('assistant', `Could not start the interview: ${err instanceof Error ? err.message : 'Please retry.'}`);
    } finally { setBusy(false); }
  }, [appendBubble]);

  const answerInterview = useCallback(async (value: unknown) => {
    if (!interview) return;
    setBusy(true);
    try {
      const q = interview.brandStep && interview.complete ? interview.brandStep : interview.next;
      if (!q) return;
      const response = await subscriberChatApi.interviewAnswer(interview.state, q.id, value) as InterviewSession;
      setInterview({ ...response, state: response.state });
    } catch (err) {
      setInterview(prev => prev ? { ...prev, error: `Answer failed: ${err instanceof Error ? err.message : 'Retry to continue.'}` } : prev);
    } finally { setBusy(false); }
  }, [interview]);
  const assumeInterview = useCallback(async (a: { field: string; value: unknown }) => {
    if (!interview) return;
    setBusy(true);
    try { const response = await subscriberChatApi.interviewAssume(interview.state, a.field, a.value) as InterviewSession; setInterview({ ...response, state: response.state }); }
    catch (err) { setInterview(prev => prev ? { ...prev, error: `Correction failed: ${err instanceof Error ? err.message : 'Retry to continue.'}` } : prev); }
    finally { setBusy(false); }
  }, [interview]);
  const finalizeInterview = useCallback(async () => {
    if (!interview) return;
    setBusy(true);
    try {
      const result = await subscriberChatApi.interviewManifest(interview.state, '', interview.brandTemplate) as { manifest: Record<string, unknown>; state: Record<string, unknown> };
      setInterview(prev => prev ? { ...prev, manifest: result.manifest, state: result.state } : prev);
      const buildResult = await subscriberChatApi.startFactoryBuild({ capabilityManifest: result.manifest, projectId: session?.projectId ?? undefined, initiatingChatSessionId: session?.chatSessionId ?? undefined });
      setBuild({ factoryBuildId: buildResult.factoryBuildId, specSessionId: buildResult.sessionId, specStatus: 'in_progress', specEstimatedMinutes: null, pipelineRunId: null, appName: 'your app' });
      setMode('build_progress');
    } catch (err) { setInterview(prev => prev ? { ...prev, error: `Build could not start: ${err instanceof Error ? err.message : 'Retry to continue.'}` } : prev); }
    finally { setBusy(false); }
  }, [interview, session?.projectId]);

  // resumability state is always persisted on the correct chat session.
  const pendingEntryStarted = useRef(false);
  useEffect(() => {
    if (!session || pendingEntryStarted.current) return;
    const idea = sessionStorage.getItem(`subscriber-chat:factory-idea:${chatSessionId ?? ''}`);
    if (!idea) return;
    pendingEntryStarted.current = true;
    sessionStorage.removeItem(`subscriber-chat:factory-idea:${chatSessionId ?? ''}`);
    void startWizard(idea);
  }, [session, chatSessionId, startWizard]);

  // ocn-S818-P0 Part 1: "I Have a Repo" entry-card handoff, mirroring the
  // factory-idea handoff immediately above. Runs in two hops since the entry
  // card navigates to the BARE unscoped route before a project/chat exists:
  // (a) here, on the bare route, take the pending repo + create the
  // project+chat atomically (subscriberChatApi.createEntry, kind='forge' --
  // Part 3/R1.1's ONE transaction) and navigate to the now-scoped route;
  // (b) the ingest-working-state effect below picks up on that scoped route.
  const pendingEntryRepoStarted = useRef(false);
  useEffect(() => {
    if (!session || chatSessionId || pendingEntryRepoStarted.current) return;
    const repo = takePendingEntryRepo();
    if (!repo) return;
    pendingEntryRepoStarted.current = true;
    (async () => {
      try {
        const entry = await subscriberChatApi.createEntry({ kind: 'forge', repoUrl: `https://github.com/${repo.fullName}` });
        window.dispatchEvent(new Event('ocn:projects-changed'));
        sessionStorage.setItem(`subscriber-chat:pending-repo-ingest:${entry.chatSessionId}`, JSON.stringify(repo));
        onNavigate?.(`subscriber-chat:chat:${entry.chatSessionId}`);
      } catch (err) {
        appendBubble('assistant', `I couldn't connect that repository — tell me about it in your own words. (${err instanceof Error ? err.message : String(err)})`);
      }
    })();
  }, [session, chatSessionId, onNavigate, appendBubble]);

  // ocn-S818-P0 Part 1 (continued): the codebase-ingest working state, once
  // the project+chat exist (scoped route). No deep analysis pipeline exists
  // yet -- buildRepoIngestResult reshapes what was already fetched for the
  // repo picker (name/description/language) and defers everything else as a
  // genuine open_question, converging on the SAME gap-asking pattern the
  // spec-upload path (handleFileAttach, below) established.
  const pendingRepoIngestStarted = useRef(false);
  useEffect(() => {
    if (!session || !chatSessionId || pendingRepoIngestStarted.current) return;
    const raw = sessionStorage.getItem(`subscriber-chat:pending-repo-ingest:${chatSessionId}`);
    if (!raw) return;
    pendingRepoIngestStarted.current = true;
    sessionStorage.removeItem(`subscriber-chat:pending-repo-ingest:${chatSessionId}`);
    let repo: PendingEntryRepo;
    try {
      repo = JSON.parse(raw);
    } catch {
      return;
    }
    setExtracting(true);
    appendBubble('assistant', `Connected ${repo.fullName}.`);
    const ingest = buildRepoIngestResult(repo);
    // Assistant bubbles render as markdown: a blank line before the list and "- " items
    // keep each item on its own line (single newlines and "•" collapsed into one line).
    const foundLines = ingest.summary_items.map((item) => `- ${item.label}: ${item.value}`).join('\n');
    appendBubble('assistant', `Here's what I understood from your repo:\n\n${foundLines}`);
    appendBubble('assistant', `A couple of things I still need:\n\n${ingest.open_questions.map((q) => `- ${q}`).join('\n')}`);
    setMode('app_management');
    setExtracting(false);
  }, [session, chatSessionId, appendBubble]);

  // ocn-S816-P0: the extraction seam. .md/.txt/.html are read client-side
  // (unchanged); .pdf/.docx route through the existing S569 wizard/parse-spec
  // endpoint (api.wizardParseSpec) and are reduced to a text digest so BOTH
  // file kinds feed the SAME startWizardFromSpec enrichment call -- one seam,
  // not a fork per file type.
  const handleFileAttach = useCallback(async (file: File) => {
    const lower = file.name.toLowerCase();
    const isTextFile = ALLOWED_SPEC_EXT.some((ext) => lower.endsWith(ext));
    const isDocFile = DOC_SPEC_EXT.some((ext) => lower.endsWith(ext));
    if (!isTextFile && !isDocFile) {
      appendBubble('assistant', 'I can read .md, .txt, .html, .pdf, or .docx spec files today. Please upload one of those, or just describe your app in the box below.');
      return;
    }

    appendBubble('user', `Uploaded spec: ${file.name}`);
    setExtracting(true);
    setBusy(true);
    try {
      let specText: string;
      if (isTextFile) {
        let raw: string;
        try {
          raw = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result ?? ''));
            reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
            reader.readAsText(file);
          });
        } catch (err) {
          appendBubble('assistant', `I couldn't read that file automatically — tell me about it in your own words. (${err instanceof Error ? err.message : String(err)})`);
          return;
        }
        specText = raw.trim();
        if (!specText) { appendBubble('assistant', 'That file looks empty — please upload a spec with some content.'); return; }
        if (specText.length > SPEC_MAX_CHARS) { appendBubble('assistant', `That spec is a bit long (${specText.length.toLocaleString()} characters). Please trim it under ${SPEC_MAX_CHARS.toLocaleString()} characters.`); return; }
      } else {
        const parsed = await api.wizardParseSpec(file);
        specText = specDigestFromParsedSpec(parsed);
        if (!specText.trim()) {
          appendBubble('assistant', "I couldn't read that automatically — tell me about it in your own words.");
          return;
        }
      }

      const res = await subscriberChatApi.startWizardFromSpec(specText, file.name);
      if (res.confirmationRequired && res.status === 'awaiting_confirmation') {
        setWizard({ sessionId: res.sessionId, questions: res.questions, answers: res.answers, currentIndex: 0, status: 'awaiting_confirmation', requirements: null });
        persistWizardState(res.sessionId);
        setMode('app_wizard');
        setConfirmationSummary(res.summary ?? null);
        setConfirmationVisible(true);
        // ocn-S816-P0 §B.5: continue by asking ONLY about genuine gaps —
        // computed deterministically from the SAME response, never a second
        // LLM call. Empty gap list = an honest "nothing left to ask".
        const extraction = buildSpecExtractionResult(res.summary, res.enrichedRequirements);
        appendBubble('assistant', extraction.open_questions.length > 0
          ? `A couple of things I still need:\n\n${extraction.open_questions.map((q) => `- ${q}`).join('\n')}`
          : "I think I have everything I need — take a look below and let me know if anything's off.");
      } else {
        appendBubble('assistant', "I processed your spec but couldn't prepare a confirmation. Please try again or describe your app in the box.");
      }
    } catch (err) {
      // Extraction failure/timeout: never a dead end -- degrade to normal
      // chat with the doc already attached (the user bubble above) and let
      // the subscriber continue in their own words.
      appendBubble('assistant', `I couldn't read that automatically — tell me about it in your own words. (${err instanceof Error ? err.message : String(err)})`);
    } finally {
      setExtracting(false);
      setBusy(false);
    }
  }, [appendBubble, persistWizardState]);

  // ocn-S816-P0: "I Have a Spec" entry-card handoff. The file was stashed
  // (module-level, in-memory) before navigating here so the entry screen
  // never blocks on upload/extraction. Runs the SAME handleFileAttach path a
  // mid-chat attachment uses -- one seam, not a fork.
  const pendingEntrySpecStarted = useRef(false);
  useEffect(() => {
    if (!session || pendingEntrySpecStarted.current) return;
    const file = takePendingEntrySpecFile();
    if (!file) return;
    pendingEntrySpecStarted.current = true;
    void handleFileAttach(file);
  }, [session, handleFileAttach]);

  const submitAnswer = useCallback(async (question: WizardQuestion, answer: unknown) => {
    if (!wizard) return;
    const display = formatAnswerForDisplay(question, answer);
    appendBubble('user', display);

    const nextAnswers = { ...wizard.answers, [question.id]: answer };
    setBusy(true);
    try {
      const res = await subscriberChatApi.answerWizard(wizard.sessionId, nextAnswers);
      const nextIndex = wizard.currentIndex + 1;

      if (res.confirmationRequired && res.status === 'awaiting_confirmation') {
        setWizard({
          sessionId: wizard.sessionId,
          questions: res.questions,
          answers: res.answers,
          currentIndex: nextIndex,
          status: 'awaiting_confirmation',
          requirements: null,
        });
        persistWizardState(wizard.sessionId);
        setConfirmationSummary(res.summary ?? null);
        setConfirmationVisible(true);
      } else if (res.status === 'complete' && res.enrichedRequirements) {
        // Fallback: session reached complete without a confirmation gate.
        setWizard({
          sessionId: wizard.sessionId,
          questions: res.questions,
          answers: res.answers,
          currentIndex: nextIndex,
          status: 'complete',
          requirements: res.enrichedRequirements,
        });
        persistWizardState(wizard.sessionId);
        setReviewing(true);
      } else {
        setWizard({
          sessionId: wizard.sessionId,
          questions: res.questions,
          answers: res.answers,
          currentIndex: nextIndex,
          status: res.status,
          requirements: null,
        });
        persistWizardState(wizard.sessionId);
      }
    } catch (err) {
      appendBubble('assistant', `Sorry, something went wrong: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }, [wizard, appendBubble, persistWizardState]);

  // ─── Build flow ─────────────────────────────────────────────

  const confirmBuild = useCallback(async () => {
    if (!wizard) return;
    // ocn: in-chat paywall soft-gate. An unpaid subscriber (no Stripe customer)
    // can go all the way through the wizard, but starting the build routes them
    // to plan selection instead of firing. The server also enforces this
    // (402 plan_required), so this is UX, not the security boundary.
    if (!subscriber?.stripe_customer_id) {
      appendBubble('assistant', "You're all set — choose a plan to start building. Opening plans now…");
      onNavigate?.('plan');
      return;
    }
    setBusy(true);
    try {
      // Step 1: confirm the wizard session before starting spec generation.
      const confirmRes = await subscriberChatApi.wizardConfirm({
        sessionId: wizard.sessionId,
        action: 'accept',
      });
      if (confirmRes.status !== 'complete') {
        appendBubble('assistant', 'Confirmation failed — please try again.');
        return;
      }

      // Step 2: start the factory build.
      const appName =
        confirmationSummary?.productName ??
        confirmationSummary?.oneLineDescription?.split(' ').slice(0, 4).join(' ') ??
        (wizard.requirements ? inferAppName(wizard.requirements) : 'Your App');

      const res = await subscriberChatApi.startFactoryBuild({ wizardSessionId: wizard.sessionId, projectId: session?.projectId ?? undefined, initiatingChatSessionId: session?.chatSessionId ?? undefined });
      setConfirmationVisible(false);
      setReviewing(false);
      setMode('build_progress');
      const tracker: BuildTracker = {
        factoryBuildId: res.factoryBuildId,
        specSessionId: res.sessionId,
        specStatus: 'in_progress',
        specEstimatedMinutes: null,
        pipelineRunId: null,
        appName,
      };
      setBuild(tracker);
      // Persist immediately: the server has already committed the factory_build
      // row by the time /spec-generator/start returns, so if the async spec-gen
      // fails after this point the subscriber can still resume into the project.
      persistBuildState(tracker);
      persistWizardState(null);
      // ocn-S800-P0: clear the LOCAL wizard object too, not just the server
      // pointer — a lingering `wizard` object with status 'awaiting_answers'
      // is what let composer input misroute to the wizard-answer endpoint
      // after a build had already started (root cause of the S796 bug).
      setWizard(null);
      appendBubble('assistant', "Starting your build now — I'll update you as I go.");
    } catch (err) {
      appendBubble('assistant', `I couldn't start the build: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }, [wizard, confirmationSummary, appendBubble, persistBuildState, persistWizardState, subscriber, onNavigate]);

  const editConfirmation = useCallback(async (edits: Partial<EnrichedRequirements>) => {
    if (!wizard) return;
    try {
      const res = await subscriberChatApi.wizardConfirm({
        sessionId: wizard.sessionId,
        action: 'edit',
        edits,
      });
      setConfirmationSummary(res.summary ?? null);
    } catch (err) {
      appendBubble('assistant', `Edit failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [wizard, appendBubble]);

  // Poll the factory_build aggregate. ETag/304 keeps the dashboard cheap
  // even at a 4s cadence — the server returns an empty body when nothing
  // has changed since the last poll.
  useEffect(() => {
    if (!build?.factoryBuildId) return;

    let stopped = false;
    let etag: string | null = null;
    let consecutiveFailures = 0;
    let retryDelayMs = 1000;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const factoryBuildId = build.factoryBuildId;
    const appName = build.appName;
    const terminal = new Set(['live', 'failed', 'cancelled']);

    const schedule = () => {
      if (!stopped) retryTimer = setTimeout(() => { void poll(); }, retryDelayMs);
    };

    const poll = async () => {
      if (stopped) return;
      try {
        const { view, etag: nextEtag } = await subscriberChatApi.getFactoryBuild(factoryBuildId, etag);
        await refreshRailwayHistory();
        if (stopped) return;
        consecutiveFailures = 0;
        retryDelayMs = 1000;
        // A 304 is still a successful server reconciliation: clear the
        // connection warning even though there is no new projection body.
        setProgress(prev => prev ? { ...prev, connectionLost: false } : prev);
        if (nextEtag) etag = nextEtag;
        if (view) {
          setProgress({ ...factoryBuildToProgress(view, appName), connectionLost: false, lastUpdatedAt: view.updatedAt ?? new Date().toISOString() });
          if (terminal.has(view.status)) {
            // ocn-S792-P0: the terminal chat bubble (below) must land exactly
            // once per build even if this branch is somehow re-entered within
            // the same mount (e.g. a StrictMode dev double effect-invoke).
            const alreadyNotified = terminalNotifiedBuildIdsRef.current.has(factoryBuildId);
            terminalNotifiedBuildIdsRef.current.add(factoryBuildId);
            if (view.status === 'live') {
              const liveUrl = (view.deployment as Record<string, unknown> | null)?.live_url;
              setLiveApp({
                appName,
                url: typeof liveUrl === 'string' ? liveUrl : null,
                buildStartedAt: view.createdAt ? new Date(view.createdAt).getTime() : Date.now(),
              });
              setMode('app_management');
              if(session?.entryKind==='build')void setSessionMode('app_management').then(()=>refreshSession()).catch(()=>{
                appendBubble('assistant','Your build is ready, but this chat could not refresh its repository context. Reopen this same chat to verify it before starting more work.');
              });
              setBuild(null);
              persistBuildState(null); // shipped → clear resume pointer
              setProgress(null);
              void refreshApps();
              if (!alreadyNotified) appendBubble('assistant', `🎉 ${appName} is ready!`);
            } else if (view.status === 'failed' || view.status === 'cancelled') {
              if (!alreadyNotified) {
                const errMsg = (view.error as Record<string, unknown> | null)?.message
                  ?? (view.error as Record<string, unknown> | null)?.reason;
                appendBubble('assistant', view.status === 'failed'
                  ? `The build hit a problem${errMsg ? `: ${String(errMsg)}` : ''}`
                  : 'Build cancelled.');
              }
              setBuild(null);
              persistBuildState(null); // terminal → clear resume pointer
              // Keep the terminal projection on screen: it is the detail and
              // recovery surface, not a transient notification.
            }
            return;
          }
        }
      } catch {
        // Keep the last server projection visible, but make its staleness
        // explicit. Retry forever so API container swaps self-heal.
        consecutiveFailures += 1;
        retryDelayMs = Math.min(30_000, retryDelayMs * 2);
        if (consecutiveFailures >= 3) {
          setProgress(prev => prev ? { ...prev, connectionLost: true } : prev);
        }
      }
      schedule();
    };

    void poll();
    return () => { stopped = true; if (retryTimer) clearTimeout(retryTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [build?.factoryBuildId, refreshRailwayHistory]);

  // ─── Composer ───────────────────────────────────────────────

  const runSubscriberCommand = useCallback(async (command: Command) => {
    await executeCommand(command, {
      projectId,
      sync: async () => { await api.syncRun(projectId!, 'chat_command'); },
      guardian: async () => { await api.triggerGuardian(projectId!); },
      refresh: async () => { await api.getProject(projectId!); },
    });
  }, [projectId]);

  // ocn-S800-P0: `retry` re-sends an existing failed bubble in place (same id
  // + idempotency key) instead of appending a new one. Failure anywhere in
  // this function marks that ORIGINAL user bubble 'failed' with a Retry
  // affordance — the message is never silently discarded.
  const sendMessage = useCallback(async (msg: string, retry?: { bubbleId: string; idempotencyKey: string }, requestedFix?: { fixTaskIds: string[]; scope: string }) => {
    if (!session) return;
    if (requestedFix && requestedFix.scope !== composerScopeRef.current) return;
    const requestedIds = [...(requestedFix?.fixTaskIds ?? [])];
    const bubbleId = retry?.bubbleId ?? nextBubbleId();
    const idempotencyKey = retry?.idempotencyKey ?? newIdempotencyKey();
    if (retry) {
      setBubbles(prev => prev.map(b => (b.id === bubbleId ? { ...b, status: undefined } : b)));
    } else {
      setBubbles(prev => [...prev, { id: bubbleId, role: 'user', kind: 'text', text: msg, idempotencyKey }]);
    }
    const markFailed = () => setBubbles(prev => prev.map(b => (b.id === bubbleId ? { ...b, status: 'failed' as const } : b)));

    const instructions = investigationInstructions(msg);
    if (instructions !== null) {
      if (!instructions.trim()) {
        appendBubble('assistant', 'Use /investigate followed by a question about this repository.');
        return;
      }
      if (instructions.length > CODEX_MAX_INSTRUCTIONS) {
        appendBubble('assistant', `Investigation requests are limited to ${CODEX_MAX_INSTRUCTIONS.toLocaleString()} characters.`);
        return;
      }
      // The key derives from this message's key, so retrying the same bubble
      // reconciles the original start rather than launching a second run.
      const codexKey = codexIdempotencyKey(idempotencyKey);
      const pending = pendingCodexStartRef.current;
      const active = codexReceiptRef.current
        && ['submitted', 'queued', 'running', 'reconciliation_pending', 'cancel_requested'].includes(codexReceiptRef.current.status);
      // One investigation per chat at a time; only a retry of the same pending request may proceed.
      if (codexBusyRef.current || active || (pending && pending.key !== codexKey)) {
        appendBubble('assistant', 'An investigation is already in progress in this chat. Refresh, stop or check it before starting another.');
        return;
      }
      // The server holds an unconfirmed start this browser cannot replay: never start a second one.
      if (!pending && serverUnconfirmedKeyRef.current && serverUnconfirmedKeyRef.current !== codexKey) {
        appendBubble('assistant', 'An earlier investigation request in this chat could not be confirmed. Use Check again on the investigation card to confirm it before starting another, so it is never started twice.');
        return;
      }
      // A retry of the same message keeps its depth and consent; a new message uses the current depth.
      const retry = pending && pending.key === codexKey ? pending : null;
      const tier: CodexTier = retry?.tier ?? codexTierRef.current;
      const generation = ++investigationGenerationRef.current;
      setInvestigation('checking');
      // Preflight, then grant, then (deep only) explicit consent for THIS run: nobody is asked to consent
      // to a run that cannot start, and nothing is sent without consent.
      const gate = await gateInvestigationStart(session.sessionId, tier, {
        priorDeepConsent: retry?.deepConsent === true,
        confirmDeep: () => window.confirm('Run a Deep investigation? Deep runs take longer and can use substantially more credits. You are asked before every deep run.'),
        isCurrent: () => generation === investigationGenerationRef.current,
      });
      if (!gate) return;
      if (!gate.ok) {
        if (gate.state === 'deep_declined') {
          setInvestigation(null);
          appendBubble('assistant', 'Deep investigation not started. Choose another depth, or confirm to run it.');
        } else setInvestigation(gate.state);
        return;
      }
      const deepConsent = gate.deepConsent;
      setInvestigation('available');
      const outcome = await runCodexStart(session.sessionId, generation,
        { instructions, key: codexKey, tier, ...(deepConsent ? { deepConsent: true } : {}) });
      // An uncertain start keeps this bubble's Retry, which re-sends the SAME key.
      if (!outcome.ok && outcome.failure === 'uncertain') markFailed();
      return;
    }

    // Typed slash text never reaches the model. It is echoed above, then uses
    // precisely the same registry run path as a project-page action button.
    if (msg.startsWith('/')) {
      const command = commandFromText(msg);
      if (!command) {
        appendBubble('assistant', 'Unknown command. Type / to see available commands.');
        return;
      }
      try {
        await runSubscriberCommand(command);
        appendBubble('assistant', `${command.label} started.`);
      } catch (err) {
        markFailed();
        appendBubble('assistant', `Could not run ${command.label}: ${err instanceof Error ? err.message : 'Please try again.'}`);
      }
      return;
    }

    // ocn-S278-P0: opening autonomy sequence intercept — runs before any other routing.
    if (autonomySeq.turn === 1) {
      const lean = leanFromAnswer(msg);
      setAutonomySeq({ turn: 2, pendingLean: lean });
      appendBubble('assistant', buildTurn2(lean));
      return;
    }
    if (autonomySeq.turn === 2) {
      const level = autonomySeq.pendingLean ?? 'review_and_approve';
      setAutonomySeq({ turn: 0 });
      // Persist the resolved level.
      void setAutonomy(level);
      appendBubble('assistant', buildTurn3(level));
      // After sequence: if we came in via an entry-point mode, open the right flow.
      if (initialMode === 'app_wizard') {
        appendBubble('assistant', "Now let's build something. Describe your app idea and I'll get started — or upload a spec.");
      } else if (initialMode === 'sync_find_fix') {
        appendBubble('assistant', initialProjectId
          ? "I've connected your repo. I'll run a full architecture review — looking for bugs, security issues, and tech debt. Starting now."
          : "Which app would you like me to review and improve?");
      }
      return;
    }

    // ocn-S278-P0 escape hatch: honor a mid-chat autonomy change at any time.
    // Even a 'just_build' subscriber can say "show me the details" / "let me
    // approve this" (or a 'review_and_approve' one can say "just do it") and we
    // flip autonomy_level immediately via setAutonomy, then acknowledge.
    if (autonomySeq.turn === 0 && session.autonomyLevel != null) {
      const intent = detectAutonomyIntent(msg);
      if (intent && intent !== session.autonomyLevel) {
        void setAutonomy(intent);
        appendBubble('assistant', intent === 'review_and_approve'
          ? 'Got it — I\'ll surface changes for your approval from here on. (Say "just do it" any time to switch back.)'
          : 'Got it — I\'ll build and report outcomes from here on, no approvals needed. (Say "show me the details" any time to switch back.)');
        return;
      }
    }

    // Fix mode (a chat on a connected repo): never run the new-app wizard. Checked
    // issues go straight to per-task remediation (each opens a PR on the repo);
    // anything else is answered in project context. Canary 2026-10-03: 'fix them
    // all' + 17 checked issues became a >=600-char message and started the
    // build wizard ('Let's get this right', 'What are you building?').
    if ((session as { chatMode?: string }).chatMode === 'sync_find_fix') {
      const ids = hasFixIntent(msg) ? requestedIds : [];
      setBusy(true);
      try {
        if (ids.length > 0) {
          appendBubble('assistant', `Starting fixes for ${ids.length} issue${ids.length === 1 ? '' : 's'}. Each fix is made on its own branch, opened as a pull request on your repo, and merged for you. Changes to Stripe code or database migrations wait for your approval.`);
          const startedIds: string[] = [];
          const failed: string[] = [];
          let alreadyRunning = 0;
          for (const id of ids) {
            if (requestedFix?.scope !== composerScopeRef.current) break;
            try { await executeRemediationTask(id); startedIds.push(id); }
            catch (err) {
              const reason = err instanceof Error ? err.message : String(err);
              // Only typed running/PR conflicts are existing work; other 409s remain failures.
              if (isFixAlreadyRunning(err)) alreadyRunning += 1; else failed.push(reason);
            }
          }
          if (requestedFix?.scope !== composerScopeRef.current) return;
          appendBubble('assistant', fixStartMessage({ requested: ids.length, started: startedIds.length, alreadyRunning, failed }));
          // Started issues are no longer offered to a later typed 'fix all'.
          checklistTasksRef.current = checklistTasksRef.current.filter((task) => !startedIds.includes(task.id));
          const watchProjectId = fixProjectIdRef.current;
          if (watchProjectId && startedIds.length > 0) watchFixesRef.current(watchProjectId, startedIds);
        } else if (hasMergeIntent(msg) && fixProjectIdRef.current) {
          // 'Merge the open PRs': the customer asking is the approval.
          const mergeProjectId = fixProjectIdRef.current;
          const merge = await mergeOpenFixPullRequests(mergeProjectId, msg);
          const repoUrl = await api.getProject(mergeProjectId)
            .then((project) => repoWebUrl((project as unknown as { repo?: { owner?: string | null; name?: string | null } }).repo))
            .catch(() => null);
          appendBubble('assistant', merge.accepted ? 'Your merge request is queued. Fix activity will show each pull request outcome as checks complete.' : mergeResultsMessage(merge.results, repoUrl));
        } else {
          await answerHelp(msg);
        }
      } catch (err) {
        markFailed();
        appendBubble('assistant', `Sorry, I couldn't start that: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setBusy(false);
        refreshFixTasks();
      }
      return;
    }

    // If the wizard is active, text typed into the composer answers the
    // current text-type question. Otherwise, route through /decide-mode.
    // ocn-S800-P0: gated through the pure shouldRouteComposerAsWizardAnswer —
    // a build in progress always wins over stale/lingering wizard state.
    if (wizard && shouldRouteComposerAsWizardAnswer(wizard.status, reviewing, isBuildActive)) {
      const q = wizard.questions[wizard.currentIndex];
      if (q && q.type === 'text') {
        await submitAnswer(q, msg);
        return;
      }
    }

    // ocn-S568-P0: a long, detailed message is an app idea/spec, not a chat
    // question. The single-line intent regexes in /decide-mode don't match a
    // multi-paragraph paste (so it was misrouted to generic KB help), and
    // /decide-mode rejects very long input outright. Route a substantial paste
    // straight into the build wizard so intake acks it and starts the flow.
    // ocn-S800-P0: also gated on !isBuildActive — a build in progress must
    // never let a long paste silently spin up a second wizard.
    if (!wizard && !isBuildActive && msg.length >= 600) {
      setBusy(true);
      try {
        await startWizard(msg);
        await refreshSuggestions();
      } catch (err) {
        markFailed();
        appendBubble('assistant', `Sorry, I had trouble getting started: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setBusy(false);
      }
      return;
    }

    setBusy(true);
    try {
      const decision = await subscriberChatApi.decideMode(msg, session.sessionId);
      if (decision.shouldStartWizard) {
        // The first Factory message gets a project and a real thread immediately.
        // Navigate before starting the wizard so persisted wizard/build state belongs
        // to that thread even if the tab closes during intake.
        if (!projectId && !chatSessionId) {
          const entry = await subscriberChatApi.createEntry({ kind: 'factory', message: msg });
          sessionStorage.setItem(`subscriber-chat:factory-idea:${entry.chatSessionId}`, msg);
          onNavigate?.(`subscriber-chat:chat:${entry.chatSessionId}`);
          return;
        }
        if (decision.suggestedReply) appendBubble('assistant', decision.suggestedReply);
        await startWizard(msg);
      } else if (decision.mode === 'app_management' && apps.length > 0) {
        setMode('app_management');
        appendBubble('assistant', "Here are your apps:");
      } else if (decision.mode === 'build_progress') {
        setMode('build_progress');
        appendBubble('assistant', build
          ? `Your build is ${progress?.displayPercent ?? 0}% complete.`
          : "You don't have a build in progress right now.");
      } else if (decision.mode === 'technical_support') {
        // Answer the help request from the wiki/KB instead of a canned prompt.
        await answerHelp(msg);
      } else if (decision.mode === 'sync_find_fix') {
        // A repository URL begins the Forge path. A bare "sync" intent remains
        // deliberately clarifying rather than guessing which repo to bind.
        const repoUrl = msg.match(/(?:https?:\/\/|git@)github\.com[/:][^\s]+/i)?.[0];
        if (!projectId && !chatSessionId && repoUrl) {
          const entry = await subscriberChatApi.createEntry({ kind: 'forge', repoUrl });
          window.dispatchEvent(new Event('ocn:projects-changed'));
          onNavigate?.(`subscriber-chat:entry:${entry.chatSessionId}:sync:${entry.projectId}`);
          return;
        }
        // S238-SEAM: sync-drive / find-fix dispatch will be wired here by ocn-S238.
        // Guide the subscriber to select an app so S238 can target the right project.
        setMode('app_management');
        appendBubble('assistant', apps.length > 0
          ? "I can help improve or fix your app. Which one, and what would you like to change?"
          : "Tell me about the app you'd like to build and I'll get started.");
      } else {
        // No build/management/sync signal — treat it as a question and answer
        // it from the knowledge base (common questions, "how does X work", etc.).
        await answerHelp(msg);
      }
      await refreshSuggestions();
    } catch (err) {
      markFailed();
      appendBubble('assistant', `Sorry, I didn't catch that: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }, [
    session, wizard, reviewing, isBuildActive, apps.length, build, progress?.overallProgress,
    appendBubble, submitAnswer, startWizard, refreshSuggestions, answerHelp, runSubscriberCommand, refreshFixTasks,
  ]);

  // ocn-S800-P0: re-send a failed bubble in place. No-op while busy (the
  // existing double-submit guard) or if the bubble is gone/not actually failed.
  const retryFailedMessage = useCallback((bubbleId: string) => {
    if (busy) return;
    const bubble = bubbles.find((b) => b.id === bubbleId);
    if (!bubble || bubble.status !== 'failed') return;
    void sendMessage(bubble.text, { bubbleId, idempotencyKey: bubble.idempotencyKey ?? newIdempotencyKey() });
  }, [busy, bubbles, sendMessage]);

  const handleSend = useCallback((allowEmpty = false) => {
    const typed = draft.trim();
    const riskRequest = riskRepairRequestRef.current;
    const riskTargets = riskRequest?.targets.length && typed.includes(riskRequest.prompt)
      ? riskRequest.targets : riskRepairTargetsFromPrompt(typed);
    let targetIds: string[] | undefined;
    if (riskTargets) {
      const resolved = resolveRiskRepairTasks(riskTargets, checklistTasksRef.current);
      if (resolved.error) { appendBubble('assistant', resolved.error); return; }
      targetIds = resolved.ids;
    }
    const submission = composeFixSubmission({ typed, tasks: checklistTasksRef.current,
      checkedIds: checklistIdsRef.current, checkedDraft: checklistDraftRef.current, allowEmpty,
      projectFixMode: (session as { chatMode?: string } | null)?.chatMode === 'sync_find_fix', targetIds });
    if (submission.error) { appendBubble('assistant', submission.error); return; }
    const msg = submission.message;
    if (!msg || !session) return;

    setDraft('');
    riskRepairRequestRef.current = null;
    if (busy) {
      pendingMessagesRef.current.push({ text: msg, fixTaskIds: submission.fixTaskIds, scope: composerScope });
      setQueuedMessageCount(pendingMessagesRef.current.length);
      return;
    }
    void sendMessage(msg, undefined, { fixTaskIds: submission.fixTaskIds, scope: composerScope });
  }, [busy, draft, sendMessage, session, appendBubble, composerScope]);

  useEffect(() => {
    if (busy || pendingMessagesRef.current.length === 0) return;
    const nextMessage = takeScopedComposerMessage(pendingMessagesRef.current, composerScope);
    setQueuedMessageCount(pendingMessagesRef.current.length);
    if (nextMessage) {
      void sendMessage(nextMessage.text, undefined, nextMessage);
    }
  }, [busy, sendMessage, composerScope]);

  const handleSuggestion = useCallback(async (s: Suggestion) => {
    if (s.action === 'show_examples') {
      appendBubble(
        'assistant',
        'Here are a few ideas to get you thinking — tap any to use it:',
        'examples',
      );
      return;
    }
    if (s.action === 'start_new_app') {
      // ocn-S568-P0: this CTA is a prompt, not idea text. Seeding the composer
      // with the button's own visible label ("Start building my first app")
      // made that label get sent and treated as the app idea. Instead, clear
      // the composer and ask the subscriber to describe the app they want.
      setDraft('');
      appendBubble(
        'assistant',
        "Let's build it. Describe the app you want in plain English — what should it do, and who is it for? (Or paste a spec.)",
      );
      return;
    }
    if (s.action === 'open_app' && apps[0]?.url) {
      window.open(apps[0].url, '_blank', 'noopener');
      return;
    }
    setDraft(s.text);
  }, [apps, appendBubble]);

  const handleOnboardingDismiss = useCallback(async () => {
    if (!session) return;
    setDismissedBanner(true);
    try {
      await subscriberChatApi.advanceOnboarding(session.sessionId, true);
    } catch {
      // best effort — local dismissal stands
    }
  }, [session]);

  const handleExampleChip = useCallback((idea: string) => {
    setDraft(idea);
  }, []);

  // ─── Autonomy card click handler ──────────────────────────
  const handleAutonomyChoice = useCallback((choice: 'just_build' | 'review_and_approve') => {
    if (autonomySeq.turn !== 1) return;
    const label = choice === 'just_build' ? 'Just build it' : 'I\'ll review changes';
    appendBubble('user', label);
    setAutonomySeq({ turn: 2, pendingLean: choice });
    appendBubble('assistant', buildTurn2(choice));
  }, [autonomySeq.turn, appendBubble]);

  // ─── Render ─────────────────────────────────────────────────

  // ocn-S321-P0: builder tier uses full-bleed root (App.tsx removes the maxWidth wrapper).
  const activeRootStyle: React.CSSProperties = isBuilder ? rootStyleFullBleed : rootStyle;

  if (loading) {
    return <div style={activeRootStyle}><ChatLoadingState onRetry={refreshSession} /></div>;
  }
  if (error || !session) {
    return (
      <div style={activeRootStyle}>
        <RecoveryCard
          reasonCode={errorCode}
          onRetry={refreshSession}
          onBackToDashboard={() => onNavigate?.('overview')}
          onStartNewChat={() => onNavigate?.('subscriber-chat')}
        />
      </div>
    );
  }

  const onboarding = (session.onboardingState ?? {}) as { step?: string; skipped?: boolean };
  const showOnboarding = !dismissedBanner
    && !onboarding.skipped
    && apps.length === 0
    && (!onboarding.step || onboarding.step === 'welcome' || onboarding.step === 'first_app');

  const currentQuestion = wizard && !reviewing && wizard.status === 'awaiting_answers'
    ? wizard.questions[wizard.currentIndex]
    : null;

  // ocn-S789-P0: artifacts can't exist without a project, so the toggle (and
  // the split panel it opens) are both gated on the chat actually belonging
  // to one — persisted "open" state from an earlier project-bound chat is
  // simply inert until a project is attached.
  const showArtifactsToggle = Boolean(session.projectId);

  const chatColumn = (
    <>
      <header style={headerStyle}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div>
              {session.projectName && <div style={{ ...dimStyle, marginBottom: 2 }}>{session.projectName}</div>}
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{displayTitle}</h1>
            </div>
            <button onClick={() => void renameChat()} style={iconButtonStyle} type="button" title="Rename chat" aria-label="Rename chat">✎</button>
          </div>
          <p style={{ ...dimStyle, margin: '4px 0 0' }}>
            {mcpContext ? 'MCP context: ask for subscriber-safe work activity, usage, or approval context' :
              mode === 'build_progress' ? 'Building your app...' :
              mode === 'app_management' ? 'Manage your apps' :
                (projectId ?? session?.projectId) ? 'Fix and improve your existing repo: check issues, review PRs, ask what to fix next' : 'Describe an idea, I build the app'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {showArtifactsToggle && (
            <button type="button" onClick={toggleArtifactsDrawer} style={{ ...iconButtonStyle, width: 'auto', gap: 6, padding: '7px 10px' }} aria-expanded={artifactsDrawerOpen} aria-controls="subscriber-chat-artifacts" title="Project artifacts">
              <PanelRight size={16} aria-hidden="true" /> Artifacts
            </button>
          )}
          <div style={menuTriggerStyle} ref={shareMenuRef}>
            <button onClick={() => setShareMenuOpen((open) => !open)} style={iconButtonStyle} type="button" title="Share chat" aria-label="Share chat" aria-expanded={shareMenuOpen} aria-haspopup="menu">
              <Share2 size={16} aria-hidden="true" />
            </button>
            {shareMenuOpen && <div role="menu" aria-label="Share chat options" style={menuStyle}>
              <button role="menuitem" onClick={() => { setShareMenuOpen(false); void copyTranscript(); }} style={menuItemStyle} type="button"><Copy size={14} aria-hidden="true" /> Copy chat</button>
              <button role="menuitem" onClick={() => { setShareMenuOpen(false); void emailTranscript(); }} style={menuItemStyle} type="button"><Mail size={14} aria-hidden="true" /> Email chat</button>
            </div>}
          </div>
          <label style={dimStyle} htmlFor="level-select">Detail:</label>
          <select
            id="level-select"
            value={session.subscriberLevel}
            onChange={(e) => { void setLevel(e.target.value as SubscriberLevel); }}
            style={selectStyle}
          >
            <option value="non_technical">Just the outcome</option>
            <option value="business_technical">Outcome + plain-English context</option>
            <option value="technical">Full technical detail</option>
          </select>
        </div>
      </header>

      {mcpContext && <div className="mcp-chat-context" role="note"><Cable size={15} /><div><strong>OCNexus MCP context</strong><span>Read-first workspace guidance. A compatible client handles authorization; work-changing actions stay with OCNexus confirmation.</span></div><button type="button" onClick={() => onNavigate?.('mcp-connect')}>Review scope <ChevronRight size={14} /></button></div>}

      {exportStatus && <div role="status" aria-live="polite" style={{ ...dimStyle, fontSize: 12 }}>{exportStatus}</div>}

      {showOnboarding && !isBuildActive && (
        <OnboardingBanner
          onSeeExamples={() => {
            appendBubble(
              'assistant',
              'Here are a few ideas to get you thinking — tap any to use it:',
              'examples',
            );
          }}
          onDismiss={handleOnboardingDismiss}
        />
      )}

      {/* ocn-S793-P0: minHeight:0 is required for a flex child to actually
          shrink and let overflowY:auto scroll internally instead of growing
          past its flex-basis and pushing the composer out of view. */}
      <div data-testid="chat-thread-scroll" style={{ ...threadStyle, ...(isBuilder ? { overflowY: 'auto', minHeight: 0 } : {}) }}>
        {bubbles.map(b => (
          <ChatBubbleView
            key={b.id}
            bubble={b}
            onExample={handleExampleChip}
            onAutonomyChoice={handleAutonomyChoice}
            onRetry={retryFailedMessage}
          />
        ))}

        {/* ocn-S816-P0 §B.3: extraction working state -- the existing S795/S800
            progress idiom (elapsed heartbeat, no fabricated steps). */}
        {extracting && (
          <div role="status" aria-live="polite" style={{ ...dimStyle, display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
            <Loader2 size={14} className="spin" aria-hidden="true" />
            <span>Reading your spec…{extractionElapsedSec >= 3 ? ` (${extractionElapsedSec}s)` : ''}</span>
          </div>
        )}

        {busy && !extracting && <ChatBusyIndicator queuedMessageCount={queuedMessageCount} />}
        {fixWatchView && !busy && (
          <div role="status" aria-live="polite" data-testid="fix-progress" style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 12, background: 'var(--surface-raised, #1f2937)', color: 'var(--text-dim, #d1d5db)', fontSize: 13 }}>
            <Loader2 size={14} className="spin" aria-hidden="true" />
            <span>{fixProgressLabel(fixWatchView)}</span>
          </div>
        )}

        {interview && <InterviewCard session={interview} busy={busy} onAnswer={(value) => void answerInterview(value)} onAssume={(a) => void assumeInterview(a)} onFinalize={() => void finalizeInterview()} />}
        {interview?.error && <div role="alert" style={{ ...sectionStyle, color: 'var(--danger, #c33)' }}>{interview.error} <button type="button" onClick={() => interview.next && void answerInterview(undefined)} style={ghostButtonStyle}>Retry</button></div>}

        {currentQuestion && !interview && (
          <WizardQuestionCard
            question={currentQuestion}
            disabled={busy}
            totalQuestions={wizard!.questions.length}
            currentIndex={wizard!.currentIndex}
            onAnswer={(answer) => submitAnswer(currentQuestion, answer)}
          />
        )}

        {confirmationVisible && (
          <WizardConfirmationCard
            summary={confirmationSummary}
            disabled={busy}
            onConfirm={confirmBuild}
            onEdit={editConfirmation}
          />
        )}

        {reviewing && wizard?.requirements && (
          <RequirementsReviewCard
            requirements={wizard.requirements}
            disabled={busy}
            onConfirm={confirmBuild}
            onCancel={() => {
              setReviewing(false);
              setWizard(null);
              appendBubble('assistant', 'No problem — tell me what you\'d like to change and we can adjust.');
            }}
          />
        )}

        {/* ocn-S321-P0 contract #3: artifact cards are collapsible inline per the layout contract. */}
        {progress && (
          <CollapsibleArtifact label={`Building ${progress.appName}…`} defaultOpen>
            <BuildProgressCard
              progress={progress}
              onStartNewBuild={() => {
                setProgress(null);
                setBuild(null);
                setMode('app_wizard');
                appendBubble('assistant', 'What would you like to build next?');
              }}
            />
          </CollapsibleArtifact>
        )}

        {liveApp && (
          <CollapsibleArtifact label={`${liveApp.appName} is live`} defaultOpen>
            <AppLiveCard
              liveApp={liveApp}
              onOpen={() => liveApp.url && window.open(liveApp.url, '_blank', 'noopener')}
              onBuildAnother={() => {
                setLiveApp(null);
                setMode('app_wizard');
                appendBubble('assistant', 'Awesome — what would you like to build next?');
              }}
              onModify={() => {
                appendBubble(
                  'assistant',
                  `Tell me what you'd like to change about ${liveApp.appName} and I'll update it.`,
                );
              }}
            />
          </CollapsibleArtifact>
        )}

        {mode === 'app_management' && apps.length > 0 && (
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>Your apps</h2>
            <div style={{ display: 'grid', gap: 10 }}>
              {apps.map(app => <AppCard key={app.appId} app={app} />)}
            </div>
          </section>
        )}

      {(investigation || codexReceipt) && (
        <CodexInvestigationCard state={investigation ?? undefined} receipt={codexReceipt ?? undefined}
          busy={codexBusy} onRefresh={codexReceipt ? () => { void refreshCodexRun(); } : undefined}
          onCancel={codexReceipt ? () => { void stopCodexRun(); } : undefined}
          onCheckAgain={pendingCodexStart ? () => { void checkCodexStartAgain(); }
            : serverUnconfirmedKey ? () => { void reconcileCodexStart(); } : undefined}
          onDiscard={pendingCodexStart || serverUnconfirmedKey ? () => { void discardCodexStart(); } : undefined}
          onConnect={() => { void connectCodex(); }} />
      )}
      <div ref={threadEndRef} data-testid="chat-thread-end" />
      {emptyChatEntry(projectId ?? session?.projectId ?? null, bubbles.length, isBuildActive, session?.chatMode) === 'project-checklist' ? (
        // Findings share the message thread's scroll area so a long checklist
        // cannot consume a separate flex allocation and collapse chat history.
        <div data-testid="project-checklist-scroll" style={{ flex: '0 0 auto' }}>
          {session?.sessionId && (
            <LiveCodexSessionPanel key={`live-codex:${session.sessionId}`} projectId={(projectId ?? session?.projectId)!} onNavigate={onNavigate} />
          )}
          <ProjectFixActivity key={`activity:${subscriber?.id}:${projectId ?? session?.projectId}`} projectId={(projectId ?? session?.projectId)!} refreshKey={fixActivityRevision} onOutcomeChange={refreshChecklist} />
          <ProjectFixChecklist key={`checklist:${composerScope}`} projectId={(projectId ?? session?.projectId)!} selectionScope={composerScope} refreshKey={checklistRevision} disabled={busy} onDraft={(message: string) => { if (busy || !session) { setDraft(message); return; } void sendMessage(message, undefined, { fixTaskIds: [...checklistIdsRef.current], scope: composerScope }); }} onSelectionChange={(selection: string | null) => { checklistDraftRef.current = selection; }} onSelectedIdsChange={(ids: string[]) => { checklistIdsRef.current = ids;  }} onTasksChange={(tasks: ProjectSyncTask[]) => { checklistTasksRef.current = tasks; }} />
        </div>
      ) : emptyChatEntry(projectId ?? session?.projectId ?? null, bubbles.length, isBuildActive, session?.chatMode) === 'entry-cards' ? (
        <EntryCards
          hasProjects={apps.length > 0}
          onNewIdea={() => { setMode('app_wizard'); appendBubble('assistant', "What would you like to build?"); }}
          onSpec={() => fileInputRef.current?.click()}
          onRepo={() => onNavigate?.('onboarding-wizard')}
          onModify={() => setMode('app_management')}
        />
      ) : null}


      </div>

      {!isBuildActive && suggestions.length > 0 && !(projectId ?? session?.projectId) && (
        // The entry cards already offer 'Change one of my existing apps'; do not
        // repeat the same chip from the server suggestions right under them.
        <SuggestionsBar suggestions={bubbles.length === 0 && apps.length > 0 ? suggestions.filter((s) => s.id !== 'modify-app') : suggestions} onPick={handleSuggestion} />
      )}

      <section className="subscriber-chat-composer" style={composerStyle}>
        <input ref={fileInputRef} type="file" accept=".md,.txt,.html,.pdf,.docx,text/markdown,text/plain,text/html" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFileAttach(f); e.target.value = ''; }} />
        {!slashMenuDismissed && draft.startsWith('/') && (
          <SlashCommandMenu input={draft} selectedIndex={slashSelectedIndex} projectSelected={Boolean(projectId)} onSelect={(command) => { setDraft(`/${command.name}`); setSlashMenuDismissed(true); setSlashSelectedIndex(0); }} />
        )}
        <textarea
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setSlashMenuDismissed(false); setSlashSelectedIndex(0); }}
          onKeyDown={(e) => {
            const commands = commandsMatchingSlash(draft);
            if (!slashMenuDismissed && draft.startsWith('/') && commands.length) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setSlashSelectedIndex((index) => Math.max(0, Math.min(commands.length - 1, index + (e.key === 'ArrowDown' ? 1 : -1))));
                return;
              }
              if (e.key === 'Escape') { e.preventDefault(); setSlashMenuDismissed(true); return; }
              if (e.key === 'Enter') { e.preventDefault(); const command = commands[slashSelectedIndex] ?? commands[0]; setDraft(`/${command.name}`); setSlashMenuDismissed(true); return; }
            }
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend(); }
          }}
          placeholder={currentQuestion && currentQuestion.type === 'text'
            ? 'Type your answer...'
            : (session as { chatMode?: string } | null)?.chatMode === 'sync_find_fix'
              ? 'Ask about your repo, or use Fix selected in the checklist...'
              : 'Describe what you want to build in plain English...'}
          style={textareaStyle}
          aria-label="Chat message"
          rows={2}
        />
        <div style={composerControlsRowStyle}>
          <div style={menuTriggerStyle}>
            <button onClick={() => setAddMenuOpen((open) => !open)} disabled={busy} style={iconButtonStyle} type="button" title="Add to chat" aria-label="Add to chat" aria-expanded={addMenuOpen} aria-haspopup="menu">
              {busy ? <InlineBusyMark /> : <Plus size={18} aria-hidden="true" />}
            </button>
            {addMenuOpen && <div role="menu" aria-label="Add to chat options" style={{ ...menuStyle, bottom: 'calc(100% + 6px)', top: 'auto', left: 0 }}>
              <button role="menuitem" onClick={() => { setAddMenuOpen(false); fileInputRef.current?.click(); }} style={menuItemStyle} type="button"><Plus size={14} aria-hidden="true" /> Upload spec</button>
              {!projectId && !isBuildActive && (
                <button role="menuitem" onClick={() => { setAddMenuOpen(false); onNavigate?.('welcome-connect'); }} style={menuItemStyle} type="button"><GitBranch size={14} aria-hidden="true" /> Connect existing repository</button>
              )}
            </div>}
          </div>
          {draft.startsWith('/investigate') && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, marginLeft: 'auto', marginRight: 8 }}>
              Depth
              <select aria-label="Investigation depth" value={codexTier} disabled={busy || codexBusy}
                onChange={(e) => { const next = codexTiers.find((t: CodexTierOption) => t.id === e.target.value); if (next) setCodexTier(next.id); }}>
                {codexTiers.map((t: CodexTierOption) => (
                  <option key={t.id} value={t.id}>{t.requiresConsent ? `${t.label} (confirm each run)` : t.label}</option>
                ))}
              </select>
            </label>
          )}
          <ChatComposerSubmit draft={draft} busy={busy} onSend={() => { void handleSend(); }} />
        </div>
      </section>
    </>
  );

  // ocn-S793-P0: pin the composer to the bottom / maximize the thread feedback
  // area for the builder tier. root (rootStyleFullBleed) is already a
  // display:flex column filling the available height; wrapping chatColumn in
  // the same flex-column-with-minHeight:0 shape as the split-panel-open path
  // (chatColumnStyle) makes the 3-zone pin (header natural / thread flex:1
  // scroll / composer natural, anchored last) apply identically whether the
  // artifacts panel is open or closed, instead of only when it's open.
  return (
    <div style={{ ...activeRootStyle, cursor: busy ? 'wait' : undefined }} aria-busy={busy}>
      {artifactsDrawerOpen && session.projectId ? (
        <div style={{ ...splitRowStyle, ...(isBuilder ? { overflow: 'hidden' } : {}) }}>
          <div style={{ ...chatColumnStyle, ...(isBuilder ? { overflow: 'hidden' } : {}) }}>{chatColumn}</div>
          <div id="subscriber-chat-artifacts" role="complementary" style={artifactsPanelWrapperStyle}>
            <ArtifactsPanel chatMode projectId={session.projectId} width="min(420px, 40vw)" onClose={toggleArtifactsDrawer} />
          </div>
        </div>
      ) : isBuilder ? (
        <div style={{ ...chatColumnStyle, overflow: 'hidden' }}>{chatColumn}</div>
      ) : chatColumn}
    </div>
  );
}

function InlineBusyMark(): React.ReactElement {
  return <span aria-hidden="true" style={{ marginRight: 5, display: 'inline-block' }}>◌</span>;
}

// Shared assistant-side affordance for every operation that uses the page busy state.
function ChatBusyIndicator({ queuedMessageCount }: { queuedMessageCount: number }): React.ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
        padding: '9px 12px', borderRadius: 12, background: 'var(--surface-raised, #1f2937)',
        color: 'var(--text-dim, #d1d5db)', fontSize: 13,
      }}
    >
      {/* ocn-S588-P0: shared animated-dots treatment — same class reused by the
          stage stepper's active-stage indicator (Part 1) for a consistent feel. */}
      <span className="subscriber-animated-dots" aria-hidden="true">
        <span /><span /><span />
      </span>
      <span>Working…{queuedMessageCount > 0 ? ` ${queuedMessageCount} message${queuedMessageCount === 1 ? '' : 's'} queued.` : ''}</span>
    </div>
  );
}

// ─── CollapsibleArtifact (ocn-S321-P0 contract #3) ───────────
// Wraps an artifact card (build progress, app live) so it can be
// collapsed and dismissed inline in the chat thread. Reuses the
// S310 expand/collapse pattern for visual consistency.

function CollapsibleArtifact({
  label, children, defaultOpen = true,
}: {
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ alignSelf: 'stretch', width: '100%' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-dim, #9ca3af)', fontSize: 'var(--text-xs, 12px)',
          fontWeight: 600, padding: '4px 0', marginBottom: open ? 6 : 0,
        }}
        aria-expanded={open}
      >
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {label}
      </button>
      {open && children}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

export function ChatBubbleView({ bubble, onExample, onAutonomyChoice, onRetry }: {
  bubble: ChatBubble;
  onExample: (idea: string) => void;
  onAutonomyChoice: (choice: 'just_build' | 'review_and_approve') => void;
  onRetry: (bubbleId: string) => void;
}): React.ReactElement {
  // ocn-S310-P0: findings detail renders inline-expandably per contract #4.
  // Default collapsed: show summary line only. Expand IN-THREAD for full detail.
  const [findingsExpanded, setFindingsExpanded] = React.useState(false);
  // ocn-S346-P0: arch_review bubble -- three-register narrative, inline-expandable.
  const [archExpanded, setArchExpanded] = React.useState(false);

  if (bubble.kind === 'arch_review') {
    // Text format (from the backend comm layer):
    //   Line 1: headline (default collapsed summary)
    //   Remainder: full three-register narrative + roadmap (expanded in-thread)
    // The full structured report is surfaced separately as a collapsible artifact
    // (S321 rule 2) -- never the default view.
    const newlineIdx = bubble.text.indexOf('\n');
    const headlineLine = newlineIdx >= 0 ? bubble.text.slice(0, newlineIdx) : bubble.text;
    const detailText = newlineIdx >= 0 ? bubble.text.slice(newlineIdx + 1) : '';
    return (
      <div style={assistantBubbleStyle}>
        <p style={{ margin: 0 }}>{headlineLine}</p>
        {detailText && (
          <>
            <button
              type="button"
              onClick={() => setArchExpanded(e => !e)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--accent, #00E5FF)', fontSize: 'var(--text-xs, 12px)',
                fontWeight: 600, padding: '4px 0', marginTop: 4,
              }}
              aria-expanded={archExpanded}
            >
              {archExpanded ? 'Collapse review' : 'Show full walkthrough'}
              <span style={{ fontSize: 10 }}>{archExpanded ? '▲' : '▼'}</span>
            </button>
            {archExpanded && (
              <pre style={{
                margin: '8px 0 0', whiteSpace: 'pre-wrap', fontSize: 'var(--text-xs, 12px)',
                lineHeight: 1.6, fontFamily: 'inherit', color: 'var(--text-dim, #9ca3af)',
                borderTop: '1px solid var(--border)', paddingTop: 8,
              }}>
                {detailText.trim()}
              </pre>
            )}
          </>
        )}
      </div>
    );
  }

  if (bubble.kind === 'findings_detail') {
    // Split into summary (first line) and body (rest). Collapse the body by default.
    const newlineIdx = bubble.text.indexOf('\n');
    const summaryLine = newlineIdx >= 0 ? bubble.text.slice(0, newlineIdx) : bubble.text;
    const bodyText = newlineIdx >= 0 ? bubble.text.slice(newlineIdx + 1) : '';
    return (
      <div style={assistantBubbleStyle}>
        <p style={{ margin: 0 }}>{summaryLine}</p>
        {bodyText && (
          <>
            <button
              type="button"
              onClick={() => setFindingsExpanded(e => !e)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--accent, #00E5FF)', fontSize: 'var(--text-xs, 12px)',
                fontWeight: 600, padding: '4px 0', marginTop: 4,
              }}
              aria-expanded={findingsExpanded}
            >
              {findingsExpanded ? 'Hide details' : 'Show details'}
              <span style={{ fontSize: 10 }}>{findingsExpanded ? '▲' : '▼'}</span>
            </button>
            {findingsExpanded && (
              <pre style={{
                margin: '8px 0 0', whiteSpace: 'pre-wrap', fontSize: 'var(--text-xs, 12px)',
                lineHeight: 1.6, fontFamily: 'inherit', color: 'var(--text-dim, #9ca3af)',
                borderTop: '1px solid var(--border)', paddingTop: 8,
              }}>
                {bodyText.trim()}
              </pre>
            )}
          </>
        )}
      </div>
    );
  }
  if (bubble.kind === 'autonomy_choice') {
    return (
      <div style={{ alignSelf: 'flex-start', width: '100%' }}>
        <p style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 500, color: 'var(--text, #e5e7eb)' }}>
          Two ways to work together.
        </p>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-dim, #9ca3af)' }}>
          You can change this any time — just say "show me more detail" or "let me approve changes".
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {/* Card A: Just build it */}
          <div
            style={autonomyCardStyle}
            onClick={() => onAutonomyChoice('just_build')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onAutonomyChoice('just_build')}
          >
            <div style={{ ...autonomyIconBadge, background: 'rgba(0, 229, 255, 0.12)', color: '#00E5FF' }}>
              <Zap size={20} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text, #e5e7eb)' }}>Just build it</div>
            <div style={{ fontSize: 13, color: 'var(--text-dim, #9ca3af)', lineHeight: 1.5, flex: 1 }}>
              I'll handle the build and ship it. You stay out of the weeds — I surface outcomes and the occasional big decision.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#00E5FF', fontSize: 13, fontWeight: 500 }}>
              Choose this <ChevronRight size={13} />
            </div>
          </div>
          {/* Card B: Review & approve */}
          <div
            style={autonomyCardStyle}
            onClick={() => onAutonomyChoice('review_and_approve')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onAutonomyChoice('review_and_approve')}
          >
            <div style={{ ...autonomyIconBadge, background: 'rgba(139, 92, 246, 0.12)', color: '#8B5CF6' }}>
              <Eye size={20} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text, #e5e7eb)' }}>Review & approve</div>
            <div style={{ fontSize: 13, color: 'var(--text-dim, #9ca3af)', lineHeight: 1.5, flex: 1 }}>
              You see the plan and approve changes before they go live. Full visibility into every step.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#8B5CF6', fontSize: 13, fontWeight: 500 }}>
              Choose this <ChevronRight size={13} />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (bubble.kind === 'examples') {
    return (
      <div style={assistantBubbleStyle}>
        <p style={{ margin: '0 0 8px' }}>{bubble.text}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {EXAMPLE_IDEAS.map(idea => (
            <button
              key={idea}
              onClick={() => onExample(idea)}
              style={chipStyle}
              type="button"
            >
              {idea}
            </button>
          ))}
        </div>
      </div>
    );
  }
  const isUser = bubble.role === 'user';
  const isFailed = isUser && bubble.status === 'failed';
  return (
    <div style={isUser ? { ...userBubbleStyle, ...(isFailed ? failedUserBubbleStyle : {}) } : assistantBubbleStyle}>
      {isUser && <span style={userBubbleRoleStyle}>You</span>}
      {isUser ? (
        <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{bubble.text}</p>
      ) : (
        <MarkdownContent content={bubble.text} />
      )}
      {isFailed && (
        <div role="alert" style={failedBubbleFooterStyle}>
          <span>Not sent.</span>
          <button type="button" onClick={() => onRetry(bubble.id)} style={retryButtonStyle}>Retry</button>
        </div>
      )}
      {bubble.sources && bubble.sources.length > 0 && (
        <SourcesList sources={bubble.sources} />
      )}
    </div>
  );
}

// De-duplicate sources by uri (retrieval returns one row per chunk, so the
// same article can appear multiple times). Keeps the first/highest-ranked
// occurrence and drops sources with no link to follow.
export function dedupeSources(sources: AnswerSource[]): AnswerSource[] {
  const seen = new Set<string>();
  const out: AnswerSource[] = [];
  for (const s of sources) {
    if (!s.uri) continue;
    if (seen.has(s.uri)) continue;
    seen.add(s.uri);
    out.push(s);
  }
  return out;
}

// Renders the wiki/KB links an answer was grounded on. Exported for testing.
export function SourcesList({ sources }: { sources: AnswerSource[] }): React.ReactElement | null {
  const links = dedupeSources(sources);
  if (links.length === 0) return null;
  return (
    <div style={sourcesStyle}>
      <span style={sourcesLabelStyle}>Helpful articles</span>
      <ul style={sourcesUlStyle}>
        {links.map((s: AnswerSource) => (
          <li key={s.uri ?? s.title ?? ''} style={{ marginTop: 4 }}>
            <a
              href={s.uri ?? '#'}
              target="_blank"
              rel="noreferrer"
              style={sourceLinkStyle}
            >
              {s.title ?? s.uri}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OnboardingBanner({ onSeeExamples, onDismiss }: {
  onSeeExamples: () => void;
  onDismiss: () => void;
}): React.ReactElement {
  return (
    <section style={bannerStyle}>
      <div style={{ flex: 1 }}>
        <strong style={{ fontSize: 14 }}>Describe any app idea in plain English</strong>
        <p style={{ ...dimStyle, margin: '4px 0 0' }}>
          I'll ask a few quick questions, then build it for you. No technical details required.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button onClick={onSeeExamples} style={ghostButtonStyle} type="button">See examples</button>
        <button onClick={onDismiss} style={ghostButtonStyle} type="button">Dismiss</button>
      </div>
    </section>
  );
}

function WizardQuestionCard({
  question, disabled, totalQuestions, currentIndex, onAnswer,
}: {
  question: WizardQuestion;
  disabled: boolean;
  totalQuestions: number;
  currentIndex: number;
  onAnswer: (answer: unknown) => void;
}): React.ReactElement {
  const [textValue, setTextValue] = useState('');
  const [numberValue, setNumberValue] = useState<number>(10);

  useEffect(() => {
    setTextValue('');
    setNumberValue(10);
  }, [question.id]);

  return (
    <div style={wizardCardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={dimStyle}>Question {currentIndex + 1} of {totalQuestions}</span>
        {!question.required && <span style={dimStyle}>Optional</span>}
      </div>
      <p style={{ margin: '6px 0 12px', fontSize: 15, fontWeight: 500 }}>{question.question}</p>

      {question.type === 'multiple_choice' && question.options && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {question.options.map(opt => (
            <button
              key={opt}
              onClick={() => onAnswer(opt)}
              disabled={disabled}
              style={pillButtonStyle}
              type="button"
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {question.type === 'boolean' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onAnswer(true)} disabled={disabled} style={pillButtonStyle} type="button">Yes</button>
          <button onClick={() => onAnswer(false)} disabled={disabled} style={pillButtonStyle} type="button">No</button>
        </div>
      )}

      {question.type === 'number' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setNumberValue(v => Math.max(0, v - 1))}
            disabled={disabled}
            style={stepButtonStyle}
            type="button"
            aria-label="Decrease"
          >−</button>
          <input
            type="number"
            value={numberValue}
            onChange={(e) => setNumberValue(Number(e.target.value) || 0)}
            style={numberInputStyle}
            disabled={disabled}
            min={0}
          />
          <button
            onClick={() => setNumberValue(v => v + 1)}
            disabled={disabled}
            style={stepButtonStyle}
            type="button"
            aria-label="Increase"
          >+</button>
          <button
            onClick={() => onAnswer(numberValue)}
            disabled={disabled}
            style={primaryButtonStyle}
            type="button"
          >Submit</button>
        </div>
      )}

      {question.type === 'text' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && textValue.trim()) {
                e.preventDefault();
                onAnswer(textValue.trim());
              }
            }}
            placeholder="Type your answer..."
            style={textInputStyle}
            disabled={disabled}
          />
          <button
            onClick={() => textValue.trim() && onAnswer(textValue.trim())}
            disabled={disabled || !textValue.trim()}
            style={primaryButtonStyle}
            type="button"
          >Submit</button>
        </div>
      )}
    </div>
  );
}

function RequirementsReviewCard({
  requirements, disabled, onConfirm, onCancel,
}: {
  requirements: EnrichedRequirements;
  disabled: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.ReactElement {
  return (
    <div style={reviewCardStyle}>
      <h2 style={sectionTitleStyle}>Requirements review</h2>
      <p style={{ margin: '0 0 12px' }}>{requirements.summary}</p>

      <ReviewRow label="Who uses it">
        {requirements.userTypes.map(u => u.name).join(', ') || '—'}
      </ReviewRow>

      <ReviewRow label="What it does">
        <ul style={listStyle}>
          {requirements.coreFeatures.slice(0, 5).map(f => (
            <li key={f.name}><strong>{f.name}</strong> — {f.description}</li>
          ))}
        </ul>
      </ReviewRow>

      <ReviewRow label="Expected size">
        ~{requirements.expectedUsers.toLocaleString()} users ({requirements.scalingRequirements.tier} scale)
      </ReviewRow>

      {requirements.integrationNeeds.length > 0 && (
        <ReviewRow label="Integrations">
          {requirements.integrationNeeds.map(i => i.name).join(', ')}
        </ReviewRow>
      )}

      <ReviewRow label="Industry">
        {requirements.industry || '—'}
      </ReviewRow>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={onConfirm} disabled={disabled} style={primaryButtonStyle} type="button">
          Build it
        </button>
        <button onClick={onCancel} disabled={disabled} style={ghostButtonStyle} type="button">
          Not quite right
        </button>
      </div>
    </div>
  );
}

function ReviewRow({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ ...dimStyle, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 14 }}>{children}</div>
    </div>
  );
}

function AppLiveCard({
  liveApp, onOpen, onBuildAnother, onModify,
}: {
  liveApp: { appName: string; url: string | null; buildStartedAt: number };
  onOpen: () => void;
  onBuildAnother: () => void;
  onModify: () => void;
}): React.ReactElement {
  const durationMin = Math.max(1, Math.round((Date.now() - liveApp.buildStartedAt) / 60_000));
  return (
    <section style={liveCardStyle}>
      <h2 style={{ ...sectionTitleStyle, fontSize: 18 }}>🎉 {liveApp.appName} is live</h2>
      <p style={{ ...dimStyle, margin: '2px 0 12px' }}>Built in {durationMin} minute{durationMin === 1 ? '' : 's'}</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {liveApp.url ? (
          <button onClick={onOpen} style={primaryButtonStyle} type="button">Open app</button>
        ) : (
          <span style={dimStyle}>Deployment URL coming soon…</span>
        )}
        <button onClick={onBuildAnother} style={ghostButtonStyle} type="button">Build another app</button>
        <button onClick={onModify} style={ghostButtonStyle} type="button">Modify this app</button>
      </div>
    </section>
  );
}

function AppCard({ app }: { app: DeployedApp }): React.ReactElement {
  return (
    <div style={appCardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <strong>{app.name}</strong>
        <span style={{ ...dimStyle, textTransform: 'capitalize' }}>{app.status}</span>
      </div>
      {app.description && <p style={{ margin: '4px 0', fontSize: 13 }}>{app.description}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        {app.url && (
          <a href={app.url} target="_blank" rel="noreferrer" style={linkButtonStyle}>
            Open app
          </a>
        )}
        {app.canModify && <button style={ghostButtonStyle} type="button">Modify</button>}
        {app.canDuplicate && <button style={ghostButtonStyle} type="button">Duplicate</button>}
      </div>
    </div>
  );
}

export function EntryCards({ hasProjects, onNewIdea, onSpec, onRepo, onModify }: {
  hasProjects: boolean;
  onNewIdea: () => void;
  onSpec: () => void;
  onRepo: () => void;
  onModify: () => void;
}): React.ReactElement {
  return (
    <section aria-label="Start a project" style={{ ...suggestionsBarStyle, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
      <EntryCard icon={<Lightbulb size={18} />} title="New Idea" subtitle="Describe your app and I'll build it" onClick={onNewIdea} />
      <EntryCard icon={<FileText size={18} />} title="I Have a Spec" subtitle="Upload a PRD, brief, or project doc" onClick={onSpec} />
      <EntryCard icon={<GitBranch size={18} />} title="I Have a Repo" subtitle="Connect an existing codebase" onClick={onRepo} />
      {hasProjects && <button type="button" onClick={onModify} style={chipStyle}>Change one of my existing apps</button>}
    </section>
  );
}

function EntryCard({ icon, title, subtitle, onClick }: { icon: React.ReactNode; title: string; subtitle: string; onClick: () => void }): React.ReactElement {
  return (
    <button type="button" onClick={onClick} style={{ ...chipStyle, minHeight: 76, textAlign: 'left', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 600 }}>{icon}{title}</span>
      <span style={{ ...dimStyle, fontSize: 12 }}>{subtitle}</span>
    </button>
  );
}

function SuggestionsBar({ suggestions, onPick }: {
  suggestions: Suggestion[];
  onPick: (s: Suggestion) => void;
}): React.ReactElement {
  return (
    <div style={suggestionsBarStyle}>
      {suggestions.map(s => (
        <button
          key={s.id}
          onClick={() => onPick(s)}
          style={chipStyle}
          type="button"
        >
          {s.text}
        </button>
      ))}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────

function formatAnswerForDisplay(question: WizardQuestion, answer: unknown): string {
  if (question.type === 'boolean') return answer ? 'Yes' : 'No';
  if (answer === null || answer === undefined) return '—';
  return String(answer);
}

function inferAppName(req: EnrichedRequirements): string {
  const summaryWords = (req.summary ?? '').split(/\s+/).slice(0, 3).join(' ');
  if (summaryWords && summaryWords.length > 3) return summaryWords;
  if (req.coreFeatures[0]?.name) return req.coreFeatures[0].name;
  return 'your app';
}

function friendlyMinutes(mins: number): string {
  if (mins <= 1) return 'under a minute';
  if (mins < 60) return `about ${mins} minute${mins === 1 ? '' : 's'}`;
  const hours = Math.round(mins / 60);
  return `about ${hours} hour${hours === 1 ? '' : 's'}`;
}

type FriendlyPhase = 'starting' | 'analyzing' | 'implementing' | 'testing' | 'deploying';

function factoryStatusLabel(status: FactoryBuildView['status']): FriendlyPhase {
  switch (status) {
    case 'spec_generating': return 'analyzing';
    case 'spec_generated':  return 'analyzing';
    case 'building':        return 'implementing';
    case 'deploying':       return 'deploying';
    case 'live':            return 'deploying';
    case 'failed':          return 'analyzing';
    case 'cancelled':       return 'analyzing';
    default:                return 'starting';
  }
}

function factoryStatusMessage(view: FactoryBuildView, appName: string): string {
  const inFlight = view.phases.find(p => p.status === 'running' || p.status === 'claimed');
  if (inFlight) return `Building ${inFlight.componentName} — ${inFlight.phase ?? 'in progress'}`;
  switch (view.status) {
    case 'spec_generating': return `Designing ${appName} based on your answers…`;
    case 'spec_generated':  return `Planning ${appName}'s components…`;
    case 'building':        return `Building ${appName}'s components…`;
    case 'deploying':       return `Deploying ${appName}…`;
    case 'live':            return `${appName} is ready!`;
    case 'failed':          return `${appName} hit a problem.`;
    case 'cancelled':       return `${appName} build was cancelled.`;
    default:                return `Working on ${appName}…`;
  }
}

// ocn-S588-P0: the single status -> subscriber-stage map. Ground-truthed
// against the real factory_build_status DB enum (packages/store/src/
// factory-status.ts: spec_generating, spec_generated, building, deploying,
// live, failed, cancelled) — there is no separate DB "testing" status; test
// activity happens inside `building` and surfaces via the nested S585 feed.
const FACTORY_BUILD_STAGE_DEFS: ReadonlyArray<{ id: string; label: string; status: FactoryBuildView['status'] }> = [
  { id: 'understanding', label: 'Understanding your spec', status: 'spec_generating' },
  { id: 'setup', label: 'Setting up your app', status: 'spec_generated' },
  { id: 'building', label: 'Building features', status: 'building' },
  { id: 'deploying', label: 'Deploying', status: 'deploying' },
  // ocn-S731-P0: 'live' is its own terminal DB status (factory_build_status
  // enum) and deserves its own visible stepper stage — without this entry
  // `currentFactoryStageIndex`'s direct lookup never matched 'live' and fell
  // through to the fallback heuristic, landing on 'deploying' and leaving
  // the stepper's last label stuck at "Deploying" even once the app was live.
  { id: 'live', label: 'Live', status: 'live' },
];

/**
 * Which stage index is "current". For the live-mapped statuses this is a
 * direct lookup. `failed`/`cancelled` carry no dedicated stage of their own
 * (the DB enum doesn't have one) — best-effort place the terminal marker at
 * the furthest stage the build evidently reached, using the same signals
 * (deployment blob / component phases / spec path) already read elsewhere
 * in this file.
 */
export function currentFactoryStageIndex(view: FactoryBuildView): number {
  const direct = FACTORY_BUILD_STAGE_DEFS.findIndex((s) => s.status === view.status);
  if (direct >= 0) return direct;
  const hasDeployment = !!view.deployment && Object.keys(view.deployment).length > 0;
  const hasComponents = view.phases.length > 0;
  if (hasDeployment) return FACTORY_BUILD_STAGE_DEFS.findIndex((s) => s.id === 'deploying');
  if (hasComponents) return FACTORY_BUILD_STAGE_DEFS.findIndex((s) => s.id === 'building');
  if (view.specPath) return FACTORY_BUILD_STAGE_DEFS.findIndex((s) => s.id === 'setup');
  return 0;
}

export function buildFactoryStages(view: FactoryBuildView): BuildStage[] {
  const isLive = view.status === 'live';
  const isTerminalFailure = view.status === 'failed' || view.status === 'cancelled';
  const currentIndex = currentFactoryStageIndex(view);
  return FACTORY_BUILD_STAGE_DEFS.map((def, index): BuildStage => {
    let status: BuildStageStatus;
    if (isLive || index < currentIndex) status = 'complete';
    else if (index === currentIndex) status = isTerminalFailure ? 'failed' : 'active';
    else status = 'pending';
    // ocn-S795-P0: view.stages is optional (older fixtures / not-yet-refreshed
    // callers) — absent or non-matching entries just mean no sub-steps yet.
    const apiStage = view.stages?.find((s) => s.key === def.id);
    return { id: def.id, label: def.label, status, subSteps: apiStage?.subSteps ?? [] };
  });
}

const FACTORY_GENERIC_FRIENDLY_ERROR = 'This build hit a problem. You can try again or contact support.';

// ocn-S792-P0: small internal-message → friendly-message map. `error.message`
// is a raw backend-internal string (project ids, tenant ids, "for project",
// etc.) — a known internal phrase gets a subscriber-readable line instead of
// leaking implementation detail; an unmapped message still passes through
// as-is (existing behavior) rather than being blanket-suppressed, since most
// factory_builds.error messages are already reasonably plain descriptions of
// what went wrong. Extend this map as new internal-sounding failure strings
// are identified.
const FACTORY_INTERNAL_ERROR_FRIENDLY_MAP: ReadonlyArray<{ match: RegExp; friendly: string }> = [
  { match: /repo gate failed/i, friendly: 'This build needs a connected repository before it can continue.' },
];

export function factoryFriendlyError(view: FactoryBuildView): string | null {
  if (view.status !== 'failed') return null;
  const err = view.error as Record<string, unknown> | null;
  const msg = err?.message ?? err?.reason;
  if (typeof msg !== 'string' || msg.length === 0) return FACTORY_GENERIC_FRIENDLY_ERROR;
  const mapped = FACTORY_INTERNAL_ERROR_FRIENDLY_MAP.find((entry) => entry.match.test(msg));
  return mapped ? mapped.friendly : msg;
}

/**
 * ocn-S592-P0: the header/footer counters must be derived from the SAME
 * source as the visible stepper (buildFactoryStages) so they can NEVER
 * contradict it. The raw backend `progressPercent` sits at 0 through the
 * whole early-stage window, which previously made the card contradict
 * itself (stepper shows Stage 1 done, header/footer said "0% / 0 steps").
 * displayPercent is a floor, never a ceiling: it only ever raises the raw
 * percent up to what the completed-stage count already proves happened.
 */
export function deriveBuildProgressCounters(
  stages: ReadonlyArray<BuildStage>,
  rawProgressPercent: number | null | undefined,
  // ocn-S730-P0: optional third floor input. When the chain driver has
  // reported real stepIndex/totalSteps, that percent is at least as accurate
  // as the coarse 4-bucket stage floor — folded in with the same
  // never-a-ceiling discipline as the existing floor.
  chainSteps?: FactoryBuildView['chainSteps'],
): { stepsDone: number; totalStages: number; displayPercent: number } {
  const totalStages = stages.length;
  const stepsDone = stages.filter((stage) => stage.status === 'complete').length;
  const stagePercent = totalStages > 0 ? Math.round((stepsDone / totalStages) * 100) : 0;
  const chainStepsPercent = chainSteps?.percent ?? 0;
  const displayPercent = Math.max(rawProgressPercent ?? 0, stagePercent, chainStepsPercent);
  return { stepsDone, totalStages, displayPercent };
}

/**
 * ocn-S730-P0: the real per-step feed, sourced directly from factory_events
 * (chainSteps) instead of the phases[]-averaged synthesis buildProgressSteps
 * does. Returns null when there is nothing real to show yet (no trace_id, no
 * step events landed, or a legacy_worker build) — callers fall back to
 * buildProgressSteps in that case.
 */
export function buildRealStepFeed(chainSteps: FactoryBuildView['chainSteps']): BuildProgressStep[] | null {
  if (!chainSteps || chainSteps.steps.length === 0) return null;
  return chainSteps.steps.map((step): BuildProgressStep => ({
    id: `chain-step-${step.stepIndex}`,
    label: step.label ?? `Step ${step.stepIndex} of ${step.totalSteps}`,
    detail: `Step ${step.stepIndex} of ${step.totalSteps}`,
    status: step.status === 'completed' ? 'complete' : step.status === 'failed' ? 'failed' : 'active',
  }));
}

function buildProgressSteps(view: FactoryBuildView): BuildProgressStep[] {
  const orderedPhases = view.phases
    .filter((phase: FactoryBuildView['phases'][number]) => phase.startedAt !== null || phase.completedAt !== null || phase.status === 'running' || phase.status === 'claimed')
    .map((phase: FactoryBuildView['phases'][number], index: number) => ({ phase, index }))
    .sort((left: { phase: FactoryBuildView['phases'][number]; index: number }, right: { phase: FactoryBuildView['phases'][number]; index: number }) => {
      const leftTime = left.phase.startedAt ? Date.parse(left.phase.startedAt) : Number.NaN;
      const rightTime = right.phase.startedAt ? Date.parse(right.phase.startedAt) : Number.NaN;
      if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) return left.index - right.index;
      return leftTime - rightTime || left.index - right.index;
    });
  const activeIndex = orderedPhases.reduce((latest: number, entry: { phase: FactoryBuildView['phases'][number] }, index: number) => (
    entry.phase.status !== 'complete' ? index : latest
  ), -1);

  if (orderedPhases.length === 0) {
    // ocn-S792-P0: this synthetic placeholder must never claim 'active' once
    // the build is actually terminal-failed — even though today's only
    // renderer (the S585 sub-step feed) already gates on the PARENT stage's
    // own 'failed' status and so never surfaces this specific inconsistency
    // visually, a hardcoded 'active' here is still a latent trap for any
    // future reader of `progress.steps` (e.g. completedTaskCount).
    const isFailed = view.status === 'failed';
    return [{
      id: `build-${view.status}`,
      label: isFailed ? 'Build failed' : factoryStatusLabel(view.status),
      detail: null,
      status: isFailed ? 'failed' : 'active',
    }];
  }

  return orderedPhases.map(({ phase, index }: { phase: FactoryBuildView['phases'][number]; index: number }): BuildProgressStep => ({
    id: `${phase.componentName}-${phase.phase ?? ''}-${phase.startedAt ?? index}`,
    label: sanitizeShortTitle(phase.phase ?? phase.componentName),
    detail: phase.phase && phase.componentName !== phase.phase ? sanitizeShortTitle(phase.componentName) : null,
    status: phase.status === 'complete' ? 'complete' : (index === activeIndex ? 'active' : 'complete'),
  }));
}

export function factoryBuildToProgress(view: FactoryBuildView, appName: string): LiveBuildProgress {
  const cleanAppName = sanitizeShortTitle(appName);
  const phase = factoryStatusLabel(view.status);
  const remainingMins: number | null = null; // server doesn't emit ETA yet
  // ocn-S730-P0: prefer the real per-step feed; only synthesize from
  // phases[] when the chain hasn't reported any step events yet.
  const steps = buildRealStepFeed(view.chainSteps) ?? buildProgressSteps(view);
  const stages = buildFactoryStages(view);
  const { stepsDone, displayPercent } = deriveBuildProgressCounters(stages, view.progressPercent, view.chainSteps);
  return {
    runId: view.id,
    appName: cleanAppName,
    overallProgress: view.progressPercent,
    estimatedMinutesRemaining: remainingMins,
    estimatedTimeRemaining: null,
    estimatedTimeRemainingFriendly: remainingMins !== null ? friendlyMinutes(remainingMins) : null,
    currentTask: {
      name: cleanAppName,
      phase,
      progress: view.progressPercent,
      statusMessage: factoryStatusMessage(view, cleanAppName),
    },
    completedTaskCount: steps.filter((step: BuildProgressStep) => step.status === 'complete').length,
    steps,
    status: view.status,
    stages,
    currentStageIndex: currentFactoryStageIndex(view),
    friendlyError: factoryFriendlyError(view),
    stepsDone,
    displayPercent,
    chainSteps: view.chainSteps,
    activeStartedAt: view.phases.find(p => p.status === 'running' || p.status === 'claimed')?.startedAt ?? view.createdAt,
    canCancel: view.status !== 'live' && view.status !== 'failed' && view.status !== 'cancelled',
    canPause: false,
  };
}

// ─── Styles ────────────────────────────────────────────────────

const rootStyle: React.CSSProperties = {
  maxWidth: 880,
  margin: '0 auto',
  padding: '20px 16px 24px',
  fontFamily: 'var(--font-sans, system-ui)',
  color: 'var(--text, #e5e7eb)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minHeight: 'calc(100vh - 48px)',
  border: '1px solid var(--border2)',
  borderRadius: 'var(--radius-lg)',
  // Chat sits one surface step above the page (--bg -> --surface2) with a
  // stronger edge and soft shadow so it reads as its own panel.
  background: 'var(--surface2)',
  boxShadow: '0 8px 28px rgba(0, 0, 0, 0.35)',
};

// ocn-S321-P0 contract #1: full-bleed root for builder tier.
// App.tsx removes the maxWidth/padding wrapper div so this fills the available space.
const rootStyleFullBleed: React.CSSProperties = {
  padding: '16px 20px 16px',
  fontFamily: 'var(--font-sans, system-ui)',
  color: 'var(--text, #e5e7eb)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  flex: 1,
  height: '100%',
  overflow: 'hidden',
  border: '1px solid var(--border2)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--surface2)',
  boxShadow: '0 8px 28px rgba(0, 0, 0, 0.35)',
};

// ocn-S789-P0: expandable right-side artifacts split (replaces the old
// position:fixed overlay). splitRowStyle/chatColumnStyle only wrap content
// when the panel is open; when closed, chatColumn renders as the root's
// direct children exactly as before (root's own gap/flex handle layout).
const splitRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  minHeight: 0,
};

const chatColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  flex: 1,
  minWidth: 0,
  minHeight: 0,
};

const artifactsPanelWrapperStyle: React.CSSProperties = {
  flexShrink: 0,
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  paddingBottom: 12,
  borderBottom: '1px solid var(--border, #2a2f3a)',
  flexWrap: 'wrap',
  gap: 12,
};

const threadStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  flex: 1,
  paddingBottom: 8,
};

const sectionStyle: React.CSSProperties = {
  padding: 16,
  border: '1px solid var(--border, #2a2f3a)',
  borderRadius: 8,
  background: 'var(--panel, #111827)',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: 15,
  fontWeight: 600,
};

// ocn-S809-P0 (Part 4): recovery-card visual treatment, brand tokens with
// fallback hex per this file's established convention.
const recoveryCardStyle: React.CSSProperties = {
  margin: '24px auto',
  maxWidth: 440,
  padding: 20,
  borderRadius: 12,
  background: 'var(--red-dim, #2a1414)',
  border: '1px solid var(--red, #f87171)',
};

const recoveryTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--text, #e5e7eb)',
};

const recoveryActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  marginTop: 14,
  flexWrap: 'wrap',
};

const bannerStyle: React.CSSProperties = {
  ...sectionStyle,
  background: 'var(--blue-soft, #1a2332)',
  borderColor: 'var(--blue, #3b82f6)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
};

// ocn-S588-P0: single left-aligned chat column — subscriber messages stay in
// the same column as assistant messages, distinguished by fill + label only.
const userBubbleStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  maxWidth: '82%',
  padding: '8px 14px 10px',
  borderRadius: '14px 14px 14px 4px',
  background: 'var(--blue-dim, rgba(59,130,246,0.12))',
  border: '1px solid var(--blue, #3b82f6)',
  color: 'var(--text, #e5e7eb)',
  fontSize: 14,
  lineHeight: 1.4,
};

const userBubbleRoleStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 3,
  color: 'var(--blue, #3b82f6)',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.2,
};

// ocn-S800-P0: a failed-send user bubble is never silently dropped — it stays
// visible with a distinct red-tinted border + a Retry affordance.
const failedUserBubbleStyle: React.CSSProperties = {
  borderColor: 'var(--red, #f87171)',
};

const failedBubbleFooterStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 6,
  color: 'var(--red, #f87171)',
  fontSize: 12,
  fontWeight: 600,
};

const retryButtonStyle: React.CSSProperties = {
  padding: '3px 10px',
  border: '1px solid var(--red, #f87171)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--red, #f87171)',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
};

const assistantBubbleStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  maxWidth: '82%',
  padding: '10px 14px',
  borderRadius: '14px 14px 14px 4px',
  background: 'var(--panel-alt, #0f172a)',
  border: '1px solid var(--border, #2a2f3a)',
  color: 'var(--text, #e5e7eb)',
  fontSize: 14,
  lineHeight: 1.4,
};

const wizardCardStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  width: '100%',
  padding: 16,
  borderRadius: 10,
  border: '1px solid var(--blue, #3b82f6)',
  background: 'var(--panel, #111827)',
};

const reviewCardStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  width: '100%',
  padding: 18,
  borderRadius: 10,
  border: '1px solid var(--green, #22c55e)',
  background: 'var(--panel, #111827)',
};

const liveCardStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  width: '100%',
  padding: 18,
  borderRadius: 10,
  border: '1px solid var(--green, #22c55e)',
  background: 'linear-gradient(135deg, rgba(34,197,94,0.08), var(--panel, #111827))',
};

const listStyle: React.CSSProperties = {
  margin: '4px 0 0',
  paddingLeft: 18,
  fontSize: 14,
  lineHeight: 1.6,
};

const sourcesStyle: React.CSSProperties = {
  marginTop: 10,
  paddingTop: 8,
  borderTop: '1px solid var(--border, #2a2f3a)',
};

const sourcesLabelStyle: React.CSSProperties = {
  color: 'var(--text-dim, #9ca3af)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const sourcesUlStyle: React.CSSProperties = {
  margin: '4px 0 0',
  paddingLeft: 16,
  fontSize: 13,
};

const sourceLinkStyle: React.CSSProperties = {
  color: 'var(--blue, #3b82f6)',
  textDecoration: 'none',
};

const suggestionsBarStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  padding: '8px 0',
};

// ocn-S789-P0: vertical stack — full-width textarea on top, controls row below.
const composerStyle: React.CSSProperties = {
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 8,
  padding: 10,
  border: '1px solid var(--border, #2a2f3a)',
  borderRadius: 8,
  background: 'var(--panel, #111827)',
};

const composerControlsRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
};

const chipStyle: React.CSSProperties = {
  padding: '10px 14px',
  minHeight: 44,
  border: '1px solid var(--border, #374151)',
  borderRadius: 20,
  background: 'transparent',
  color: 'var(--text, #e5e7eb)',
  cursor: 'pointer',
  fontSize: 13,
};

const pillButtonStyle: React.CSSProperties = {
  ...chipStyle,
  background: 'var(--panel-alt, #0f172a)',
  fontWeight: 500,
};

const stepButtonStyle: React.CSSProperties = {
  width: 44,
  height: 44,
  border: '1px solid var(--border, #374151)',
  borderRadius: 8,
  background: 'var(--panel-alt, #0f172a)',
  color: 'var(--text, #e5e7eb)',
  cursor: 'pointer',
  fontSize: 18,
  fontWeight: 600,
};

const numberInputStyle: React.CSSProperties = {
  width: 80,
  height: 44,
  padding: '0 10px',
  border: '1px solid var(--border, #374151)',
  borderRadius: 6,
  background: 'var(--bg, #0b1020)',
  color: 'var(--text, #e5e7eb)',
  fontSize: 14,
  textAlign: 'center',
};

const textInputStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 44,
  padding: '0 12px',
  border: '1px solid var(--border, #374151)',
  borderRadius: 6,
  background: 'var(--bg, #0b1020)',
  color: 'var(--text, #e5e7eb)',
  fontSize: 14,
};

function SlashCommandMenu({ input, selectedIndex, projectSelected, onSelect }: { input: string; selectedIndex: number; projectSelected: boolean; onSelect: (command: Command) => void }): React.ReactElement | null {
  const commands = commandsMatchingSlash(input);
  if (!commands.length) return null;
  return <div role="listbox" aria-label="Commands" style={{ ...menuStyle, bottom: 'calc(100% + 6px)', top: 'auto', left: 10, right: 10, width: 'auto' }}>
    {commands.map((command, index) => {
      const disabled = command.requiresProject && !projectSelected;
      return <button key={command.name} role="option" aria-selected={index === selectedIndex} disabled={disabled} onClick={() => onSelect(command)} type="button" style={{ ...menuItemStyle, width: '100%', opacity: disabled ? .55 : 1, background: index === selectedIndex ? 'var(--surface3)' : undefined, display: 'block', textAlign: 'left' }}>
        <strong>/{command.name}</strong> — {command.description}{disabled ? ' (Select a project first)' : ''}
      </button>;
    })}
  </div>;
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 44,
  padding: 10,
  border: '1px solid var(--border, #374151)',
  borderRadius: 6,
  background: 'var(--bg, #0b1020)',
  color: 'var(--text, #e5e7eb)',
  fontFamily: 'inherit',
  fontSize: 14,
  resize: 'vertical',
};

const primaryButtonStyle: React.CSSProperties = {
  minHeight: 44,
  padding: '10px 18px',
  border: 'none',
  borderRadius: 6,
  background: 'var(--blue, #3b82f6)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
};

const menuTriggerStyle: React.CSSProperties = { position: 'relative', flexShrink: 0 };
const iconButtonStyle: React.CSSProperties = { ...ghostButtonStyle, minWidth: 44, padding: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
const menuStyle: React.CSSProperties = { position: 'absolute', zIndex: 10, top: 'calc(100% + 6px)', right: 0, minWidth: 150, padding: 4, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface2)', boxShadow: 'var(--shadow-lg)' };
const menuItemStyle: React.CSSProperties = { width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: 'none', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: 13, textAlign: 'left' };

const linkButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  textDecoration: 'none',
  display: 'inline-block',
  textAlign: 'center',
};

const selectStyle: React.CSSProperties = {
  padding: '6px 8px',
  minHeight: 36,
  border: '1px solid var(--border, #374151)',
  borderRadius: 4,
  background: 'var(--bg, #0b1020)',
  color: 'var(--text, #e5e7eb)',
  fontSize: 13,
};

const appCardStyle: React.CSSProperties = {
  padding: 12,
  border: '1px solid var(--border, #374151)',
  borderRadius: 6,
  background: 'var(--panel-alt, #0f172a)',
};

const autonomyCardStyle: React.CSSProperties = {
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 18,
  border: '1px solid var(--border, #2A2A45)',
  borderRadius: 10,
  background: 'var(--panel, #111127)',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
};

const autonomyIconBadge: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
